import {
    currentUnixTimestamp,
    delay,
    forceDownload,
    forceTextDownload,
    genId,
    getObjWithLimitedKeys,
    isBlankValue,
    isCtrlKeyDown,
    jsOpenNewTab,
    jsRedirect,
    stringToHexColor,
    toSnakeCase,
    truncateString,
} from '@/js/util.js';

import {
    assert,
    assertEquals,
    assertId,
    assertIn,
    assertInteger,
    assertNonemptyObject,
    assertNonemptyString,
    isFloatStrict,
    parseFloatStrict,
} from '@/js/util/asserts.js';

import {
    AREA_ESTIMATOR_TYPES,
    ASSET_NAME,
    AssetType,
    AttributeType,
    DERIVED_EXPORT_FIELDS,
    EXPORT_FIELDS_TO_AUTOCOLOR,
    ExportField,
    FEATURE_TYPE_MAP_SYMBOLS,
    FeatureType, FRACTION_SYMBOLS,
    HexColor,
    MAP_SYMBOL_RADIUS_COMPENSATION,
    MapControl,
    MapSymbol,
    millisecondsIn,
    NUM_COLOR_BINS,
    ProjectType,
    secondsIn,
} from '@/js/util/shared_constants.js';

import {
    apiFetch,
    getAddressFromCoords,
    getGeolocation,
    handleFetchErrors,
    save_button_click,
} from '@/js/util/shared_functionality.js';

import {
    ConfirmationModal,
    ErrorModal,
    InfoModal,
    Button,
    awaitConfirmation, modalIsOpen,
} from '@/js/classes/modal.js';

import { convertDbhFromStoredToDisplay } from '@/js/util/tree_functionality.js';
import { AssetForm } from '@/js/classes/asset_form.js';
import { Asset } from '@/js/classes/asset.js';

import { Stroke, Icon } from 'ol/style';
import { BingMaps } from 'ol/source';
import { Control } from 'ol/control';
import Google from 'ol/source/Google';
import OSM from 'ol/source/OSM'
import TileLayer from 'ol/layer/Tile';
import { fromLonLat, toLonLat, transform as projectionTransform } from 'ol/proj';
import { Feature } from 'ol';
import { LineString, Point, Polygon } from 'ol/geom';
import Collection from 'ol/Collection';
import { Modify } from 'ol/interaction';
import GeoJSON from 'ol/format/GeoJSON';
import KML from 'ol/format/KML';
import VectorSource from 'ol/source/Vector';
import { noModifierKeys } from 'ol/events/condition';
import { containsExtent } from 'ol/extent';

import {
    polygon as turfPolygon,
    multiPolygon as turfMultiPolygon,
    area as turfArea,
    difference as turfDifference,
    featureCollection as turfFeatureCollection,
    union as turfUnion,
} from '@turf/turf';

import jstsGeojsonReader from 'jsts/org/locationtech/jts/io/GeoJSONReader.js';
import jstsGeojsonWriter from 'jsts/org/locationtech/jts/io/GeoJSONWriter.js';
import jstsBufferOp from 'jsts/org/locationtech/jts/operation/buffer/BufferOp.js';

import {
    setOptions as setGoogleMapsJsApiOptions,
    importLibrary as importGoogleMapsJsApiLibrary,
} from '@googlemaps/js-api-loader';

import { captureException as sentryCaptureException } from '@sentry/browser';
import chroma from 'chroma-js';
import { cloneDeep } from 'lodash-es';
import shpwrite from '@mapbox/shp-write';


const GEOLOCATION_MAXIMUM_AGE_SECONDS = 5;
const GEOLOCATION_TIMEOUT_SECONDS = 10;
const geolocate_options = {
    enableHighAccuracy: true,
    timeout: millisecondsIn.seconds(GEOLOCATION_TIMEOUT_SECONDS),
    maximumAge: millisecondsIn.seconds(GEOLOCATION_MAXIMUM_AGE_SECONDS),
};

const map_symbol_cache = {};
const symbol_style_cache = {};
export const MapControlClass = {};


export function getGoogleLayer_labels() {
    // TODO: Add google maps logo control openlayers.org/en/latest/examples/google.html
    return new TileLayer({
        title: 'Google with Labels',
        type: 'base',
        source: new Google({
            'key': GOOGLEMAPS_KEY,
            'mapType': 'satellite',
            'layerTypes': ['layerRoadmap'],
        }),
    });
}

export function getGoogleLayer_satellite() {
    // TODO: Add google maps logo control openlayers.org/en/latest/examples/google.html
    return new TileLayer({
        title: 'Google',
        type: 'base',
        source: new Google({
            'key': GOOGLEMAPS_KEY,
            'mapType': 'satellite',
        }),
    });
}

export function getGoogleLayer_topographical() {
    // TODO: Add google maps logo control openlayers.org/en/latest/examples/google.html
    return new TileLayer({
        title: 'Google Terrain',
        type: 'base',
        source: new Google({
            'key': GOOGLEMAPS_KEY,
            'mapType': 'terrain',
            'layerTypes': ['layerRoadmap'],
        }),
    });
}

export function getOsmLayer() {
    return new TileLayer({
        title: 'OpenStreetMap',
        type: 'base',
        visible: true,
        source: new OSM(),
    });
}

export function getBingLayer_labels() {
    return new TileLayer({
        title: 'Bing with Labels',
        type: 'base',
        source: new BingMaps({
            key: BINGMAPS_KEY,
            imagerySet: 'AerialWithLabelsOnDemand',
            placeholderTiles: false,
        }),
    });
}

export function getBingLayer_satellite() {
    return new TileLayer({
        title: 'Bing',
        type: 'base',
        source: new BingMaps({
            key: BINGMAPS_KEY,
            imagerySet: 'Aerial',
            placeholderTiles: false,
        }),
    });
}

export function withTileLogging(layer) {
    const source = layer.getSource();
    const layerTitle = layer.get('title') || 'Unknown Layer';

    if(!source) {
        console.error('No source, not logging tile count.');
        return layer;
    }

    let loadedTilesCount = 0;
    let timeoutId;

    const logAndReset = () => {
        if(loadedTilesCount > 0) {
            alert(`Layer '${layerTitle}': ${loadedTilesCount} tiles loaded.`);
            loadedTilesCount = 0;
        }
    };

    const resetTimer = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(logAndReset, 3000);
    };

    source.on('tileloadstart', () => {
        resetTimer();
    });

    source.on('tileloadend', () => {
        loadedTilesCount++;
        resetTimer();
    });

    source.on('tileloaderror', () => {
        resetTimer();
    });

    return layer;
}

export const searchAddressCallback = async (view, show_coordinate_input) => {
    const search_modal = new ConfirmationModal('Search', null, 'Go');
    search_modal.show();
    search_modal.appendHtml('Loading...');
    search_modal.disableConfirmationButton();
    search_modal.startLoading();

    setGoogleMapsJsApiOptions({ key: GOOGLEMAPS_KEY });
    await importGoogleMapsJsApiLibrary('places');

    search_modal.clearBody();
    search_modal.stopLoading();
    search_modal.enableConfirmationButton();

    let coordinate_input = null;
    if(show_coordinate_input) {
        coordinate_input = document.createElement('input');
        coordinate_input.classList.add('w-100', 'mb-3');
        coordinate_input.placeholder = 'Coordinates (lat, lng)';
        search_modal.appendNode(coordinate_input);
    }

    let map_search_place = null;
    const map_search_input = new google.maps.places.PlaceAutocompleteElement({});
    search_modal.appendNode(map_search_input);

    setTimeout(() => { map_search_input.focus(); }, 50);

    map_search_input.addEventListener('gmp-select', (event) => {
        map_search_place = event.placePrediction.toPlace();
    });

    search_modal.onConfirm(async () => {
        if(show_coordinate_input && coordinate_input.value!=='') {
            try {
                const coords_parts = coordinate_input.value.split(',');
                assertEquals(coords_parts.length, 2);
                const lat = coords_parts[0].trim();
                const lng = coords_parts[1].trim();
                assertNonemptyString(lat);
                assertNonemptyString(lng);
                assert(lat > -90 && lat < 90);
                assert(lng > -180 && lng < 180);

                view.setCenter(fromLonLat([lng, lat]));
                view.setZoom(20);
                search_modal.remove();

            } catch(err) {
                new ErrorModal('Invalid coordinates format.').show();
                return;
            }
        }

        if(!map_search_place) { return search_modal.remove(); }
        try {
            await map_search_place.fetchFields({ fields: ['location'] });
            const lat = map_search_place.location.lat();
            const lng = map_search_place.location.lng();

            view.setCenter(fromLonLat([lng, lat]));
            view.setZoom(20);
            search_modal.remove();

        } catch (err) {
            new ErrorModal('Location search failed.').show();
        }
    });
}

const createCenterContainer = ((container_key, options) => {
    const container = document.createElement('div');
    container.className = `ol-center-container-${options.end} no-print`;
    container.style.position = 'absolute';
    container.style.flexWrap = 'wrap';
    container.style.left = (1 * CustomControl.DEFAULT_SIZE) + CustomControl.PADDING + 'px';
    container.style.right = (2.5 * CustomControl.DEFAULT_SIZE) + CustomControl.PADDING + 'px';
    container.style[options.end] = options.offset_y + CustomControl.PADDING + 'px';

    container.style.display = 'flex';
    container.style.justifyContent = 'center';
    container.style.gap = CustomControl.PADDING + 'px';
    container.style.pointerEvents = 'none';
    options.map_page.map.getOverlayContainerStopEvent().appendChild(container);
    options.map_page[container_key] = container;
});

export class CustomControlSpacer {
    constructor(options) {
        assert(options, 'Missing options');
        assertNonemptyObject(options.map_page);
        options.end = options.end ?? 'top';
        options.offset_x = options.offset_x ?? 0;
        options.offset_y = options.offset_y ?? 0;

        const container_key = `center_${options.end}_container`;
        if(!options.map_page[container_key]) { createCenterContainer(container_key, options); }

        const spacer_elem = document.createElement('div');
        spacer_elem.classList.add('ol-custom-control-spacer', 'no-print');

        options.map_page[container_key].appendChild(spacer_elem);
        this.spacer_elem = spacer_elem;

        this.is_showing = true;
    }

    hide() {
        if(!this.is_showing) { return; }
        this.spacer_elem.classList.add('hidden');
        this.is_showing = false;
    }

    show() {
        if(this.is_showing) { return; }
        this.spacer_elem.classList.remove('hidden');
        this.is_showing = true;
    }
}


export class CustomControl extends Control {
    static PADDING = 5;
    static DEFAULT_SIZE = 40;

    constructor(options) {
        assert(options, 'Missing options');
        assert(['left', 'right', 'center'].includes(options.side), 'options.side must be left, right, or center');
        assertNonemptyString(options.name);
        assertIn('map_page', options);

        const size = options.size ?? CustomControl.DEFAULT_SIZE;
        let height = size;
        let width = size;
        if('height' in options) { height = ((options.height * size) + (CustomControl.PADDING * (options.height - 1))); }
        if('width' in options) {
            if(isFloatStrict(options.width)) {
                width = ((options.width * size) + (CustomControl.PADDING * (options.width - 1)));
            } else {
                width = options.width;
            }
        }

        const row = options.row ?? 1;
        const column = options.column ?? 1;
        const side = options.side ?? 'left';
        const name = options.name ?? 'Unnamed';

        options.end = options.end ?? 'top';
        options.offset_x = options.offset_x ?? 0;
        options.offset_y = options.offset_y ?? 0;
        assert(['top', 'bottom'].includes(options.end), 'options.end must be top or bottom');

        const internal_name = toSnakeCase(options.name);
        const button = document.createElement('button');
        button.id = 'custom_control_button_'+internal_name;
        button.classList.add('ol-custom-control-button', 'no-print');
        button.style.justifyContent = ('text' in options) ? 'left' : 'center';
        button.title = name;

        const button_icon_elem = document.createElement('img');
        button_icon_elem.classList.add('fa-icon');
        button_icon_elem.id = 'custom_control_icon_'+internal_name;
        button_icon_elem.alt = name;
        button_icon_elem.src = `static/icons/${options.icon}.svg`;
        if(options['icon_padding']) { button_icon_elem.style.padding = options['icon_padding']; }
        button.appendChild(button_icon_elem);

        let button_text_elem = null;
        if('text' in options) {
            button_text_elem = document.createElement('span');
            button_text_elem.innerHTML = options.text;
            button.appendChild(button_text_elem);
        }

        if(height !== CustomControl.DEFAULT_SIZE || width !== CustomControl.DEFAULT_SIZE) {
            button.style.height = height+'px';
            button.style.width = (width === 'auto' ? 'auto' : width+'px');
        }

        let target_elem = options.target;

        if(side === 'center') {
            const container_key = `center_${options.end}_container`;
            if(!options.map_page[container_key]) { createCenterContainer(container_key, options); }

            target_elem = options.map_page[container_key];
            button.style.pointerEvents = 'auto';
            button.style.position = 'relative';

        } else {
            button.style[side] = options.offset_x + (((column-1) * (size + CustomControl.PADDING)) + CustomControl.PADDING)+'px';
            button.style[options.end] = options.offset_y + (((row-1) * (size + CustomControl.PADDING)) + CustomControl.PADDING)+'px';

        }

        super({
            element: button,
            target: target_elem,
        });

        this.button = button;
        this.button_icon_elem = button_icon_elem;
        this.button_text_elem = button_text_elem;
        this.options = options;
        this.map_page = options['map_page'];
        this.is_showing = true;
        this.is_showing_super = true;
        this.size = size;
    }

    setRow(new_row) {
        if(this.options.side === 'center') { return; }
        this.button.style[this.options.end] = (((new_row-1) * (this.size + CustomControl.PADDING)) + CustomControl.PADDING)+'px';
    }

    setActive() {
        this.button.style.backgroundColor = 'rgb(103, 172, 255)';
        this.button.style.border = '1px solid black';
    }

    setPassive() {
        this.button.style.backgroundColor = 'white';
        this.button.style.border = 'none';
    }

    disable() {
        this.button.disabled = true;
    }

    enable() {
        this.button.disabled = false;
    }

    hide() {
        if(!this.is_showing) { return; }
        this.button.classList.add('hidden');
        this.is_showing = false;
    }

    show() {
        if(this.is_showing) { return; }
        this.button.classList.remove('hidden');
        this.is_showing = true;
    }

    hideSuper() {
        if(!this.is_showing_super) { return; }
        this.button.classList.add('hidden-super');
        this.is_showing_super = false;
    }

    showSuper() {
        if(this.is_showing_super) { return; }
        this.button.classList.remove('hidden-super');
        this.is_showing_super = true;
    }

    setTitle(new_title) {
        this.button.title = new_title;
        this.button_icon_elem.alt = new_title;
    }

    setIcon(new_icon) {
        this.button_icon_elem.src = `static/icons/${new_icon}.svg`;
    }
}

export class CallbackControl extends CustomControl {
    constructor(options) {
        assertNonemptyString(options.side);
        assertNonemptyString(options.name);
        assertNonemptyString(options.icon);
        assertInteger(options.row);
        assert(typeof options.callback === 'function', 'CallbackControl expected function in options.callback');
        super(options);

        this.throttle_doubleclick_ms = options['throttle_doubleclick_ms'] ?? 25;
        let last_click_time = 0;

        this.button.addEventListener('click', (e) => {
            const current_time = Date.now();
            if(current_time - last_click_time < this.throttle_doubleclick_ms) { return; }
            last_click_time = current_time;
            options.callback(this, e);
        });
    }
}

MapControlClass[MapControl.ZoomIn] = class extends CallbackControl {
    constructor(options) {
        options = options ?? {};
        options.side = options.side ?? 'left';
        options.row = options.row ?? 1;
        options.name = options.name ?? 'Zoom In';
        options.icon = options.icon ?? 'plus';
        options.callback = () => {
            const view = this.getMap().getView();
            view.animate({
                zoom: view.getZoom() + 1,
                duration: 250,
            });
        };
        super(options);
    }
}

MapControlClass[MapControl.ZoomOut] = class extends CallbackControl {
    constructor(options) {
        options = options ?? {};
        options.side = options.side ?? 'left';
        options.row = options.row ?? 1;
        options.name = options.name ?? 'Zoom Out';
        options.icon = options.icon ?? 'minus';
        options.callback = () => {
            const view = this.getMap().getView();
            view.animate({
                zoom: view.getZoom() - 1,
                duration: 250,
            });
        };
        super(options);
    }
}

MapControlClass[MapControl.RotateNorth] = class extends CallbackControl {
    constructor(options) {
        options = options ?? {};
        options.side = options.side ?? 'left';
        options.row = options.row ?? 1;
        options.column = options.column ?? 2;
        options.name = options.name ?? 'Rotate North';
        options.icon = options.icon ?? 'compass';
        options.hide_when_pointing_north = options.hide_when_pointing_north ?? true;
        options.callback = (control) => {
            options.map_page.view.setRotation(0);
            if(options.hide_when_pointing_north) { control.hide(); }
        };
        super(options);

        if(!options.hide_when_pointing_north) { return; }

        const toggleControlVisibility = () => {
            const rotation_in_radians = options.map_page.view.getRotation();
            if(rotation_in_radians !== 0) {
                this.show();
            } else {
                this.hide();
            }
        }

        toggleControlVisibility();
        options.map_page.view.on('change:rotation', toggleControlVisibility);
    }
}

MapControlClass[MapControl.ToggleLegend] = class extends CallbackControl {
    hideLegend() {
        this.map_page.hideLegend();
        this.setPassive();
    }

    showLegend() {
        this.map_page.showLegend();
        this.setActive();
    }

    constructor(options) {
        options = options ?? {};
        options.side = options.side ?? 'left';
        options.row = options.row ?? 1;
        options.name = options.name ?? 'Toggle Legend';
        options.icon = options.icon ?? 'legend-icon';

        options.callback = (control) => {
            control[control.is_legend_showing ? 'hideLegend' : 'showLegend']();
            control.is_legend_showing = !control.is_legend_showing;
        };

        super(options);

        const start_shown = options['start_shown'] ?? false;
        this[start_shown ? 'showLegend' : 'hideLegend']();
        this.is_legend_showing = start_shown;
    }
}

MapControlClass[MapControl.CenterOnFeatures] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Center on Features';
        options.icon = 'center';

        options.callback = (control) => {
            if(control.map_page.staged_project.numAssets() === 0) { return; }
            const extent = control.map_page.marker_source.getExtent();
            control.map_page.view.fit(extent, {
                padding: [50, 50, 50, 50],
                duration: 250,
                maxZoom: 18,
            });
        };

        super(options);
    }
}

MapControlClass[MapControl.CenterOnFeaturesExternal] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Center on Features';
        options.icon = 'center';

        options.callback = (control) => {
            if(control.map_page.shared_map_data['assets'].length === 0) { return; }
            const extent = control.map_page.marker_source.getExtent();
            control.map_page.view.fit(extent, {
                padding: [50, 50, 50, 50],
                duration: 250,
                maxZoom: 18,
            });
        };

        super(options);
    }
}

MapControlClass[MapControl.GenerateMap] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Generate Maps';
        options.icon = 'download';

        options.callback = (control) => {
            control.map_page.exportClick();
        };

        super(options);
    }
}

MapControlClass[MapControl.MapSettings] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Map Settings';
        options.icon = 'gear';

        options.callback = (control) => {
            control.map_page.settingsClick();
        };

        super(options);
    }
}

MapControlClass[MapControl.ManageData] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Manage Data';
        options.icon = 'database-blue';

        options.callback = async (control, e) => {
            control.map_page.staged_project._last_map_centerpoint = toLonLat(control.map_page.view.getCenter());
            control.map_page.staged_project._last_map_centerpoint_timestamp = currentUnixTimestamp();
            const now = currentUnixTimestamp();
            if((now - control.map_page.staged_project._last_map_centerpoint_timestamp) > secondsIn.minutes(1)) {
                control.map_page.staged_project._last_map_centerpoint_timestamp = now;
            }
            await control.map_page.staged_project.saveOnClient();
            (isCtrlKeyDown(e) ? jsOpenNewTab : jsRedirect)('assets.html?project_id='+control.map_page.staged_project.project_id);
        };

        super(options);
    }
}

MapControlClass[MapControl.ProjectList] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Project List';
        options.icon = 'table-cropped-blue';

        options.callback = async (control, e) => {
            await control.map_page.staged_project.saveOnClient();
            (isCtrlKeyDown(e) ? jsOpenNewTab : jsRedirect)('index.html');
        };

        super(options);
    }
}

MapControlClass[MapControl.CreateLink] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Create Link';
        options.icon = 'external-link';
        options.callback = createLinkControlCallback;

        super(options);
    }
}

MapControlClass[MapControl.Save] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'right';
        options.name = 'Submit changes to Server';
        if(window.newcollector['current_collector_type'] === ProjectType.Lab) {
            options.width = options.width ?? 2;
            options.text = 'Save';
        } else {
            options.width = options.width ?? 2.5;
            options.text = 'Submit';
        }
        options.icon = 'save';
        options.callback = async (control) => {
            await save_button_click(control.map_page.staged_project, control.map_page.map_save_button, control.map_page.map_save_button_icon);
        };

        super(options);
    }
}

MapControlClass[MapControl.SearchAddress] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Search for an Address';
        options.icon = 'search';

        options.callback = async (control) => {
            searchAddressCallback(control['map_page']['view'], control['map_page']['map_settings']['search_location_with_coordinates']==='Yes');
        };

        super(options);
    }
}

MapControlClass[MapControl.LocateMe] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Locate me (via GPS)';
        options.icon = 'location-crosshairs';
        options.callback = locateMeControlCallback;
        super(options);
    }
}

MapControlClass[MapControl.LockMovement] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.row = options.row ?? 3;
        options.name = 'Lock Map Movement';
        options.icon = 'lock-movement';

        options.callback = async (control, e) => {
            this['map_page'].allow_pan[this.is_locked_from_control ? 'releaseBlock' : 'acquireBlock']('lock_movement_control');
            this['map_page'].allow_zoom[this.is_locked_from_control ? 'releaseBlock' : 'acquireBlock']('lock_movement_control');
            this[this.is_locked_from_control ? 'setPassive' : 'setActive']();
            this.is_locked_from_control = !this.is_locked_from_control;
        };

        super(options);

        this.is_locked_from_control = false;
        this.setPassive();
    }
}

MapControlClass[MapControl.HideControls] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.row = options.row ?? 1;
        options.end = options.end ?? 'bottom';
        options.offset_y = options.offset_y ?? 35;
        options.name = 'Hide Map Controls';
        options.icon = 'low-vision';
        options.exempt_control_names = options.exempt_control_names ?? ['Hide Map Controls', 'Submit changes to Server'];

        options.callback = async (control, e) => {
            const controls_currently_showing = control['controls_showing'];
            const controls = control.map_page.map.getControls().getArray();
            for(const current_control of controls) {
                if(!('options' in current_control)) { continue; }
                if(!('name' in current_control['options'])) { continue; }
                if(options.exempt_control_names.includes(current_control['options']['name'])) { continue; }
                current_control[controls_currently_showing? 'hideSuper' : 'showSuper']();
            }
            control[controls_currently_showing ? 'setActive' : 'setPassive']();
            this['controls_showing'] = !controls_currently_showing;
        };

        super(options);

        this['controls_showing'] = true;
    }
}

MapControlClass[MapControl.SizeTotals] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Show Size Totals';
        options.icon = 'calculator';

        options.callback = (async (control) => {
            const project = control.map_page.staged_project;
            const all_assets = project.getAssets();

            const totals_modal = new InfoModal(null, 'Totals');

            for(const asset_type of project.getAssetTypesPresent()) {
                const assets = all_assets.filter((asset) => asset.getType() === asset_type);
                let has_length = null;
                let has_area = null;
                let has_perimeter = null;
                let has_volume = null;
                let total_length_m = 0;
                let total_area_sq_m = 0;
                let total_perimeter_m = 0;
                let total_volume_cu_m = 0;

                for(const asset of assets) {
                    if(has_length === null) {
                        has_length = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.LengthM);
                        has_area = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.AreaSqM);
                        has_perimeter = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.PerimeterM);
                        has_volume = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.VolumeCuM);
                    }
                    if(has_length) { total_length_m += asset.getExportValue(ExportField.LengthM); }
                    if(has_area) { total_area_sq_m += asset.getExportValue(ExportField.AreaSqM); }
                    if(has_perimeter) { total_perimeter_m += asset.getExportValue(ExportField.PerimeterM); }
                    if(has_volume) { total_volume_cu_m += asset.getExportValue(ExportField.VolumeCuM); }
                }

                if(!has_length && !has_area && !has_perimeter && !has_volume) { continue; }

                const display_opts = { 'required_levels': [3], 'optional_levels': [2,4,5,6], 'max_cutoff': 50_000 };
                totals_modal.appendHtml(`
                    <h5 class="mt-2"><strong>${ASSET_NAME[asset_type]}:</strong></h5>
                    ${has_length ? `<div class="ms-2">Length: ${displayLength(total_length_m, display_opts)}</div>` : ''}
                    ${has_area ? `<div class="ms-2">Area: ${displayArea(total_area_sq_m, display_opts)}</div>` : ''}
                    ${has_perimeter ? `<div class="ms-2">Perimeter: ${displayLength(total_perimeter_m, display_opts)}</div>` : ''}
                    ${has_volume ? `<div class="ms-2">Volume: ${displayVolume(total_volume_cu_m, display_opts)}</div>` : ''}
                `);
            }

            totals_modal.show();
        });

        super(options);
    }
}

MapControlClass[MapControl.SizeTotalsOnScreen] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Show Size Totals (only on-screen features)';
        options.icon = 'calculator-center';
        options.icon_padding = '3px';

        options.callback = (async (control) => {
            const visible_asset_ids = [];
            const extent = control.map_page.view.calculateExtent(control.map_page.map.getSize());
            control.map_page.marker_source.forEachFeatureInExtent(extent, (feature) => {
                const asset_id = feature.get('asset_id');
                if(!asset_id) { return; }
                assertId(asset_id);
                if(!containsExtent(extent, feature.getGeometry().getExtent())) { return; }
                visible_asset_ids.push(asset_id);
            });

            const project = control.map_page.staged_project;
            const all_assets = project.getAssets();

            const totals_modal = new InfoModal(null, 'Totals (on-screen)');

            for(const asset_type of project.getAssetTypesPresent()) {
                const assets = all_assets.filter((asset) => asset.getType() === asset_type);
                let has_length = null;
                let has_area = null;
                let has_perimeter = null;
                let has_volume = null;
                let total_length_m = 0;
                let total_area_sq_m = 0;
                let total_perimeter_m = 0;
                let total_volume_cu_m = 0;

                for(const asset of assets) {
                    if(!visible_asset_ids.includes(asset.asset_id)) { continue; }
                    if(has_length === null) {
                        has_length = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.LengthM);
                        has_area = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.AreaSqM);
                        has_perimeter = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.PerimeterM);
                        has_volume = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.VolumeCuM);
                    }
                    if(has_length) { total_length_m += asset.getExportValue(ExportField.LengthM); }
                    if(has_area) { total_area_sq_m += asset.getExportValue(ExportField.AreaSqM); }
                    if(has_perimeter) { total_perimeter_m += asset.getExportValue(ExportField.PerimeterM); }
                    if(has_volume) { total_volume_cu_m += asset.getExportValue(ExportField.VolumeCuM); }
                }

                if(!has_length && !has_area && !has_perimeter && !has_volume) { continue; }

                const display_opts = { 'required_levels': [3], 'optional_levels': [2,4,5,6], 'max_cutoff': 50_000 };
                totals_modal.appendHtml(`
                    <h5 class="mt-2"><strong>${ASSET_NAME[asset_type]}:</strong></h5>
                    ${has_length ? `<div class="ms-2">Length: ${displayLength(total_length_m, display_opts)}</div>` : ''}
                    ${has_area ? `<div class="ms-2">Area: ${displayArea(total_area_sq_m, display_opts)}</div>` : ''}
                    ${has_perimeter ? `<div class="ms-2">Perimeter: ${displayLength(total_perimeter_m, display_opts)}</div>` : ''}
                    ${has_volume ? `<div class="ms-2">Volume: ${displayVolume(total_volume_cu_m, display_opts)}</div>` : ''}
                `);
            }

            totals_modal.show();
        });

        super(options);
    }
}

MapControlClass[MapControl.SizeTotalsAreaEstimator] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Show Size Totals (Area Estimator)';
        options.icon = 'calculator';

        options.callback = (async (control) => {
            const project = control.map_page.staged_project;
            const all_assets = project.getAssets();

            const totals_modal = new InfoModal(null, 'Totals');

            for(const estimator_type of AREA_ESTIMATOR_TYPES.AllTypes) {
                const assets = all_assets.filter((asset) => asset.getValue('estimator_type') === estimator_type);
                let has_length = null;
                let has_area = null;
                let has_perimeter = null;
                let has_volume = null;
                let total_length_m = 0;
                let total_area_sq_m = 0;
                let total_perimeter_m = 0;
                let total_volume_cu_m = 0;

                for(const asset of assets) {
                    const asset_type = asset.getType();
                    if(has_length === null) {
                        has_length = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.LengthM);
                        has_area = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.AreaSqM);
                        has_perimeter = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.PerimeterM);
                        has_volume = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.VolumeCuM);
                    }
                    if(has_length) { total_length_m += asset.getExportValue(ExportField.LengthM); }
                    if(has_area) { total_area_sq_m += asset.getExportValue(ExportField.AreaSqM); }
                    if(has_perimeter) { total_perimeter_m += asset.getExportValue(ExportField.PerimeterM); }
                    if(has_volume) { total_volume_cu_m += asset.getExportValue(ExportField.VolumeCuM); }
                }

                if(!has_length && !has_area && !has_perimeter && !has_volume) { continue; }

                const display_opts = { 'required_levels': [3], 'optional_levels': [2,4,5,6], 'max_cutoff': 50_000 };
                totals_modal.appendHtml(`
                    <h5 class="mt-2"><strong>${estimator_type}:</strong></h5>
                    ${has_length ? `<div class="ms-2">Length: ${displayLength(total_length_m, display_opts)}</div>` : ''}
                    ${has_area ? `<div class="ms-2">Area: ${displayArea(total_area_sq_m, display_opts)}</div>` : ''}
                    ${has_perimeter ? `<div class="ms-2">Perimeter: ${displayLength(total_perimeter_m, display_opts)}</div>` : ''}
                    ${has_volume ? `<div class="ms-2">Volume: ${displayVolume(total_volume_cu_m, display_opts)}</div>` : ''}
                `);
            }

            totals_modal.show();
        });

        super(options);
    }
}

MapControlClass[MapControl.SizeTotalsOnScreenAreaEstimator] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Show Size Totals (only on-screen features) (Area Estimator)';
        options.icon = 'calculator-center';
        options.icon_padding = '3px';

        options.callback = (async (control) => {
            const visible_asset_ids = [];
            const extent = control.map_page.view.calculateExtent(control.map_page.map.getSize());
            control.map_page.marker_source.forEachFeatureInExtent(extent, (feature) => {
                const asset_id = feature.get('asset_id');
                if(!asset_id) { return; }
                assertId(asset_id);
                if(!containsExtent(extent, feature.getGeometry().getExtent())) { return; }
                visible_asset_ids.push(asset_id);
            });

            const project = control.map_page.staged_project;
            const all_assets = project.getAssets();

            const totals_modal = new InfoModal(null, 'Totals (on-screen)');

            for(const estimator_type of AREA_ESTIMATOR_TYPES.AllTypes) {
                const assets = all_assets.filter((asset) => asset.getValue('estimator_type') === estimator_type);
                let has_length = null;
                let has_area = null;
                let has_perimeter = null;
                let has_volume = null;
                let total_length_m = 0;
                let total_area_sq_m = 0;
                let total_perimeter_m = 0;
                let total_volume_cu_m = 0;

                for(const asset of assets) {
                    const asset_type = asset.getType();
                    if(!visible_asset_ids.includes(asset.asset_id)) { continue; }
                    if(has_length === null) {
                        has_length = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.LengthM);
                        has_area = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.AreaSqM);
                        has_perimeter = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.PerimeterM);
                        has_volume = (asset_type in DERIVED_EXPORT_FIELDS) && DERIVED_EXPORT_FIELDS[asset_type].includes(ExportField.VolumeCuM);
                    }
                    if(has_length) { total_length_m += asset.getExportValue(ExportField.LengthM); }
                    if(has_area) { total_area_sq_m += asset.getExportValue(ExportField.AreaSqM); }
                    if(has_perimeter) { total_perimeter_m += asset.getExportValue(ExportField.PerimeterM); }
                    if(has_volume) { total_volume_cu_m += asset.getExportValue(ExportField.VolumeCuM); }
                }

                if(!has_length && !has_area && !has_perimeter && !has_volume) { continue; }

                const display_opts = { 'required_levels': [3], 'optional_levels': [2,4,5,6], 'max_cutoff': 50_000 };
                totals_modal.appendHtml(`
                    <h5 class="mt-2"><strong>${estimator_type}:</strong></h5>
                    ${has_length ? `<div class="ms-2">Length: ${displayLength(total_length_m, display_opts)}</div>` : ''}
                    ${has_area ? `<div class="ms-2">Area: ${displayArea(total_area_sq_m, display_opts)}</div>` : ''}
                    ${has_perimeter ? `<div class="ms-2">Perimeter: ${displayLength(total_perimeter_m, display_opts)}</div>` : ''}
                    ${has_volume ? `<div class="ms-2">Volume: ${displayVolume(total_volume_cu_m, display_opts)}</div>` : ''}
                `);
            }

            totals_modal.show();
        });

        super(options);
    }
}

MapControlClass[MapControl.SaveEphemeral] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Save Ephemeral Project Permanently';
        options.icon = 'save';

        options.callback = (async (control) => {
            try {
                const project = control.map_page.staged_project;

                if(!project.is_ephemeral) {
                    new ErrorModal(`
                        <h5>Project is already saved.</h5>
                        <div>ID: ${project.project_id}</div>
                    `).show();
                    return;
                }

                const properties_modal = new ConfirmationModal('Save Project', null, 'Save');

                const form_elem = document.createElement('div');
                properties_modal.appendNode(form_elem);

                const properties_form = new AssetForm({
                    'form_elem': form_elem,
                    'attributes': [
                        {
                            'attribute_type': AttributeType.String,
                            'name': 'Project Name',
                            'category_name': '',
                            'internal_name': 'name',
                            'required': true,
                        },
                        {
                            'attribute_type': AttributeType.String,
                            'name': 'Location',
                            'category_name': '',
                            'internal_name': 'location',
                            'required': true,
                        },
                        {
                            'attribute_type': AttributeType.BigString,
                            'name': 'Comments',
                            'category_name': '',
                            'internal_name': 'comments',
                            'required': false,
                        },
                    ],
                    'values': project.properties,
                });

                properties_form.generateForm();

                properties_modal.onConfirm(async () => {
                    const new_properties = properties_form.validateForm();
                    if(new_properties === false) { return; }

                    properties_modal.remove();
                    const loading_modal = new InfoModal('Saving...');
                    loading_modal.show();
                    loading_modal.disableConfirmationButton();
                    loading_modal.startLoading();

                    await project.setProperties(new_properties);
                    await project.makePermanent();
                    await project.saveProjectToServer();
                    await project.saveAllPendingOps();
                    await project.files.uploadAllAssetFiles();
                    project.saveOnClient();

                    loading_modal.stopLoading();
                    loading_modal.enableConfirmationButton();
                    loading_modal.clearBody();
                    loading_modal.appendHtml(`Project saved with ID ${project.project_id}`);
                });

                properties_modal.show();

            } catch(err) {
                new ErrorModal(`Failed to save ephemeral project.`).show();
                sentryCaptureException(err);
                console.log(err);
            }
        });

        super(options);
    }
}

MapControlClass[MapControl.DownloadGeojson] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Download GeoJson';
        options.icon = 'download-json';
        options.icon_padding = '2px';

        options.callback = ((control) => {
            const features = [];
            for(const asset of control.map_page.staged_project.getAssets()) {
                features.push({
                    'type': 'Feature',
                    'geometry': {
                        'type': asset.getFeatureType(),
                        'coordinates': asset.getValue('coordinates'),
                    },
                    'properties': asset.getExportData(true),
                });
            }

            const content = JSON.stringify(
                {
                    'type': 'FeatureCollection',
                    'features': features,
                },
                null,
                4,
            );

            forceDownload(`AreaEstimator_${currentUnixTimestamp()}.geojson`, content, 'text/json');
        });

        super(options);
    }
}

MapControlClass[MapControl.DownloadShp] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Download ShapeFile';
        options.icon = 'download-shp';
        options.icon_padding = '2px';

        options.callback = (async (control) => {
            const features = [];
            for(const asset of control.map_page.staged_project.getAssets()) {
                features.push({
                    'type': 'Feature',
                    'geometry': {
                        'type': asset.getFeatureType(),
                        'coordinates': asset.getValue('coordinates'),
                    },
                    'properties': asset.getExportData(true),
                });
            }

            const geojson = { 'type': 'FeatureCollection', 'features': features };
            const options = { 'outputType': 'blob', 'compression': 'STORE' };

            const blob = await shpwrite.zip(geojson, options);
            forceDownload(`AreaEstimator_${currentUnixTimestamp()}.zip`, blob, 'application/zip');

        });

        super(options);
    }
}

MapControlClass[MapControl.DownloadKml] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Download KML';
        options.icon = 'download-kml';
        options.icon_padding = '2px';

        options.callback = (async (control) => {
            const features = [];
            for(const asset of control.map_page.staged_project.getAssets()) {
                features.push({
                    'type': 'Feature',
                    'geometry': {
                        'type': asset.getFeatureType(),
                        'coordinates': asset.getValue('coordinates'),
                    },
                    'properties': asset.getExportData(true),
                });
            }

            const geojson = { 'type': 'FeatureCollection', 'features': features };

            const vectorSource = new VectorSource({
                features: new GeoJSON().readFeatures(geojson)
            });

            const kmlOutput = new KML().writeFeatures(vectorSource.getFeatures(), {
                featureProjection: 'EPSG:4326'
            });

            forceDownload(`AreaEstimator_${currentUnixTimestamp()}.kml`, kmlOutput, 'application/vnd.google-earth.kml+xml');

        });

        super(options);
    }
}

MapControlClass[MapControl.DeleteBoundariesAreaEstimator] = class extends CallbackControl {
    constructor(options) {
        options.side = options.side ?? 'left';
        options.name = 'Delete Boundaries';
        options.icon = 'square-xmark';
        options.icon_padding = '2px';

        options.callback = (async (control) => {
            const project = control.map_page.staged_project;
            const features = control.map_page.marker_source.getFeatures();
            for(const asset of project.getAssets()) {
                if(asset.getType() === AssetType.EstimatorLine && asset.getValue('estimator_type')==='Boundary') {
                    await project.deleteAsset(asset.asset_id);
                    const feature = features.find(feature => feature.get('asset_id') === asset.asset_id);
                    control.map_page.assetChanged(feature, asset.asset_id, true);
                }
            }
            await project.saveOnClient();
        });

        super(options);
    }
}

export const locateMeControlCallback = (async (control) => {
    const TRACKING_STATE = {
        'idle': 1,
        'initial_geolocation': 2,
        'watching': 3,
    };

    if(!control._is_setup) {
        const map = control.getMap();
        control.map = map;
        control.view = map.getView()

        control.tracking_source = getSourceByName(map, 'tracking_layer');

        control.setTrackingState = ((new_state) => {
            if(new_state === TRACKING_STATE.idle) {
                control.setPassive();
                control.enable();
            } else if(new_state === TRACKING_STATE.initial_geolocation) {
                control.disable();
                control.setActive();
            } else if(new_state === TRACKING_STATE.watching) {
                control.enable();
            }
            control.tracking_state = new_state;
        });

        control.consumePosition = ((position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const transformed_coords = projectionTransform([lng, lat], 'EPSG:4326', 'EPSG:3857');

            if(control._tracking_marker) {
                control._tracking_marker.getGeometry().setCoordinates(transformed_coords);
            } else {
                control._tracking_marker = new Feature({ geometry: new Point(transformed_coords) });
                control._tracking_marker.set('asset_id', 'tracking_marker');
                control.tracking_source.addFeature(control._tracking_marker);
            }
        });

        control.cleanUp = (() => {
            if(control.current_watch_id) { navigator.geolocation.clearWatch(control.current_watch_id); }
            control.tracking_source.removeFeature(control._tracking_marker);
            control._tracking_marker = null;
        });

        control._is_setup = true;
        control.tracking_state = TRACKING_STATE.idle;
        control.current_watch_id = null;
    }

    if(control.tracking_state === TRACKING_STATE.initial_geolocation) {
        return;

    } else if(control.tracking_state === TRACKING_STATE.watching) {
        control.cleanUp();
        control.setTrackingState(TRACKING_STATE.idle);
        return;
    }

    try {
        control.setTrackingState(TRACKING_STATE.initial_geolocation);

        let position;
        try {
            position = await getGeolocation();
        } catch(err) {
            alert(err.message);
            console.log(err);
            sentryCaptureException(err);
            control.cleanUp();
            control.setTrackingState(TRACKING_STATE.idle);
            return;
        }

        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        control.view.setCenter(projectionTransform([lng, lat], 'EPSG:4326', 'EPSG:3857'));
        control.consumePosition(position);

    } catch(err) {
        console.log(err);
        sentryCaptureException(err);
        control.cleanUp();
        control.setTrackingState(TRACKING_STATE.idle);
        return;
    }

    try {
        control.setTrackingState(TRACKING_STATE.watching);
        control.current_watch_id = navigator.geolocation.watchPosition(((current_pos) => {
            control.consumePosition(current_pos);

        }), ((err) => {
            console.log(err);
            sentryCaptureException(err);
            control.cleanUp();
            control.setTrackingState(TRACKING_STATE.idle);
        }), geolocate_options);

    } catch(err) {
        console.log(err);
        sentryCaptureException(err);
        control.cleanUp();
        control.setTrackingState(TRACKING_STATE.idle);
    }
});

export const createLinkControlCallback = (async (control) => {
    if(!('settings_values' in control)) {
        control.settings_values = {
            'shared_map_name': '',
            'expire_after': 'One Month',
            'fields_to_include': [],
        };
    }
    const share_settings_modal = new ConfirmationModal('Share Map', null, 'Publish');

    share_settings_modal.appendHtml(`
        <ul>
            <li>This map, and all selected tree properties, will be visible to anyone with the share code or link.</li>
            <li>The map will appear exactly as it does now.</li>
            <li>The user you send the map to will not be able to change the marker colors/label.</li>
        </ul>
    `);

    const settings_form_elem = document.createElement('div');
    const settings_form = new AssetForm({
        'form_elem': settings_form_elem,
        'attributes': [
            {
                'category_name': '',
                'attribute_type': AttributeType.String,
                'internal_name': 'shared_map_name',
                'name': 'Shared Map Name:',
                'required': true,
                'minLen': 0,
                'maxLen': 500,
                'placeholder': 'ex: Primary Maintenance Needs',
            },
            {
                'category_name': '',
                'attribute_type': AttributeType.ButtonSingleOption,
                'internal_name': 'expire_after',
                'required': true,
                'name': 'Expire After:',
                'options': [
                    'Two Weeks',
                    'One Month',
                    'Six Months',
                    'Never',
                ],
            },
            {
                'category_name': '',
                'attribute_type': AttributeType.ButtonMultiOption,
                'internal_name': 'fields_to_include',
                'name': 'Fields to Include:',
                'required': true,
                'options': control.map_page.staged_project.getExportFieldsSorted(AssetType.Any, true),
            },
        ],
        'values': control.settings_values,
    });
    settings_form.generateForm();
    share_settings_modal.appendNode(settings_form_elem);

    share_settings_modal.onConfirm(async () => {
        share_settings_modal.disableConfirmationButton();
        share_settings_modal.startLoading();
        await delay(25); // Allow button change to render
        const values = settings_form.validateForm();
        if(!values) {
            share_settings_modal.stopLoading();
            share_settings_modal.enableConfirmationButton();
            return;
        }
        control.settings_values = values;
        share_settings_modal.remove();

        const share_modal = new InfoModal('Uploading map state to server...','Share Map');
        share_modal.show();
        share_modal.disableConfirmationButton();
        share_modal.startLoading();

        const map_link_data = {};
        /* Generate share data */ {
            const serialized_features = [];
            for(const asset of control.map_page.staged_project.getAssets()) {
                const asset_style_properties = control.map_page.getMarkerStyleProperties(asset);
                const serialized_style = {};

                if(asset_style_properties['visual']) {
                    let visual_style_properties = asset_style_properties['visual'];
                    serialized_style['feature_type'] = asset.getFeatureType();

                    if(asset_style_properties['visual']['is_default']) {
                        visual_style_properties = control.map_page.getDefaultStyleProperties(asset.getType());
                    }

                    serialized_style['marker_symbol'] = visual_style_properties['symbol'];
                    serialized_style['marker_radius'] = visual_style_properties['radius'];
                    serialized_style['marker_fill_color'] = visual_style_properties['color'];
                    serialized_style['marker_stroke_color'] = visual_style_properties['outline_color'];
                    serialized_style['marker_stroke_width'] = visual_style_properties['outline_thickness'];
                }

                if(asset_style_properties['text']) {
                    const text_style_properties = asset_style_properties['text'];
                    serialized_style['text_content'] = text_style_properties['content'];
                    serialized_style['text_font'] = text_style_properties['font'];
                    serialized_style['text_fill_color'] = text_style_properties['color'];
                    serialized_style['text_stroke_color'] = text_style_properties['outline_color'];
                    serialized_style['text_stroke_width'] = text_style_properties['outline_thickness'];
                    serialized_style['text_offset_x'] = text_style_properties['offset_x'];
                    serialized_style['text_offset_y'] = text_style_properties['offset_y'];
                    serialized_style['text_align'] = text_style_properties['align'];
                    serialized_style['text_baseline'] = text_style_properties['baseline'];
                }

                const serialized_feature = {
                    'asset_id': asset.asset_id,
                    'asset_type': asset.getType(),
                    'asset_data': getObjWithLimitedKeys(asset.getExportData(true), values['fields_to_include']),
                    'coordinates': asset.getValue('coordinates'),
                    'map_style': serialized_style,
                };

                serialized_features.push(serialized_feature);
            }

            map_link_data['map_name'] = values['shared_map_name'];
            map_link_data['map_imagery'] = control.map_page.map_settings['map_imagery'];
            map_link_data['map_imagery_opacity'] = control.map_page.map_settings['map_imagery_opacity'];
            map_link_data['map_imagery_background'] = control.map_page.map_settings['map_imagery_background'];
            map_link_data['show_polygon_edges'] = control.map_page.map_settings['show_polygon_edges'];
            map_link_data['asset_field_order'] = {};
            for(const asset_type of control.map_page.staged_project.getAssetTypes()) {
                map_link_data['asset_field_order'][asset_type] = control.map_page.staged_project.getExportFieldsSorted(asset_type, true);
            }
            map_link_data['assets'] = serialized_features;
            map_link_data['timestamp_generated'] = currentUnixTimestamp();
            map_link_data['project_info'] = {
                'id': control.map_page.staged_project.project_id,
                'name': control.map_page.staged_project.properties.name,
                'location': control.map_page.staged_project.properties.location,
                'owner': control.map_page.staged_project.owner_email,
                'centerpoint': control.map_page.staged_project.getAssetsCenterpoint(),
            };

            const marker_color_attribute = control.map_page.map_settings_attributes.find(s => s.internal_name === 'marker_color');
            const marker_color_setting = marker_color_attribute.options[control.map_page.map_settings['marker_color']];
            if(marker_color_setting !== 'Default') {
                map_link_data['legend'] = {
                    'title': marker_color_setting,
                    'items': getLegendItems(control.map_page.staged_project, control.map_page.map_settings),
                };
            }
        }


        const response_data = await handleFetchErrors(
            apiFetch({
                path: '/reports/share_map',
                method: 'POST',
                contentType: 'application/json',
                body: JSON.stringify({
                    'project_id': control.map_page.staged_project.project_id,
                    'share_data': map_link_data,
                    'expire_after': values['expire_after'],
                    'shared_map_name': values['shared_map_name'],
                }),
                timeoutMs: 30000,
            }),
            'sharing map',
            `<div>Project ID: ${control.map_page.staged_project.project_id}</div>`,
        );

        share_modal.clearBody();

        let share_code = '??????';
        if(response_data===null) {
            share_code = genId().slice(0, 6);
            share_modal.appendHtml(`
                <h5>Uploading map state to server failed.</h5>
                <p>You can download a share file instead, and send it to the user.</p>
            `);

        } else {
            share_code = response_data['share_code'];
            const subdomain = (build_mode === 'production' ? '' : 'dev.');
            const share_link = `${subdomain}viewer.daveyinstitute.com/${share_code}`;
            share_modal.appendHtml(`
                <span style="vertical-align: center;">Map Share Code: </span><strong style="font-family: monospace; font-size: 20px;">${share_code}</strong><br>
                Map Share Link: <a href="https://${share_link}">${share_link}</a>
            `);

            const share_link_copy_button = new Button('Copy Link', ['btn-sm', 'btn-info', 'my-2']);
            share_link_copy_button.elem.style.margin = 'auto';
            share_link_copy_button.elem.style.width = '200px';
            share_link_copy_button.onClick(async () => {
                share_link_copy_button.disable();
                await navigator.clipboard.writeText('https://'+share_link);
                share_link_copy_button.flashSuccess();
                share_link_copy_button.enable();
            });
            share_modal.appendNode(share_link_copy_button.elem);

            share_modal.appendHtml(`
                <h6 class="text-center my-2">Or:</h6>
            `);
        }

        map_link_data['share_code'] = share_code;

        const share_file_download_button = new Button('Download Share File', ['btn-sm', 'btn-info', 'mb-2']);
        share_file_download_button.elem.style.margin = 'auto';
        share_file_download_button.elem.style.width = '200px';
        share_file_download_button.onClick(() => {
            share_file_download_button.disable();
            forceTextDownload('MapShareFile_'+control.map_page.staged_project.project_id+'_'+share_code+'.json', JSON.stringify(map_link_data));
            share_file_download_button.flashSuccess();
            share_file_download_button.enable();
        });
        share_modal.appendNode(share_file_download_button.elem);

        share_modal.stopLoading();
        share_modal.enableConfirmationButton();
    });

    share_settings_modal.show();
});

export function getRangeIndex(ranges, num) {
    for(let range_index = 0; range_index < ranges.length; range_index++) {
        const range = ranges[range_index];

        if(typeof range === 'number') {
            if(range_index === 0 && num <= range) {
                return range_index;
            } else if(range_index === ranges.length - 1 && num >= range) {
                return range_index;
            }

        } else if(Array.isArray(range) && range.length === 2) {
            if(num >= range[0] && num <= range[1]) {
                return range_index;
            }

        } else {
            console.error('Unhandled range:', range);
        }
    }

    return -1;
}

export function findBin(number, min, max, num_bins) {
    assert(num_bins >= 2, 'Number of bins must be at least 2');
    assert(min < max, 'Min must be less than max');

    if(number <= min) { return 0; }
    if(number >= max) { return num_bins - 1; }

    const bin_width = (max - min) / num_bins;
    const bin_index = Math.floor((number - min) / bin_width);
    return Math.min(Math.max(bin_index, 0), num_bins - 1);
}

export function calculateBinRanges(min, max, num_bins) {
    assert(num_bins >= 2, 'Number of bins must be at least 2');
    assert(min < max, 'Min must be less than max');

    const bin_width = (max - min) / num_bins;
    const bins = [];

    for(let i = 0; i < num_bins; i++) {
        const current_min = min + (i * bin_width);
        const current_max = (i === num_bins - 1) ? max : current_min + bin_width;

        bins.push({
            bin_index: i,
            min: current_min,
            max: current_max
        });
    }

    return bins;
}

export const getLegendItems = (staged_project, map_settings) => {
    const legend_items = [];
    const legend_item_limit = map_settings['legend_item_limit'];
    const count_legend_items = (map_settings['count_legend_items']==='Yes');
    const limit_legend_to_used_options = (map_settings['limit_legend_to_used_options']==='Yes');

    if(EXPORT_FIELDS_TO_AUTOCOLOR.includes(map_settings['marker_color'])) {
        const field_name = map_settings['marker_color'];
        const value_instances = {};
        for(const asset of staged_project.getAssets()) {
            const value = asset.getExportValue(field_name);
            if(isBlankValue(value)) { continue; }
            if(!(value in value_instances)) {
                value_instances[value] = 1;
            } else {
                value_instances[value]++;
            }
        }

        const autocolor_seed = map_settings['autocolor_seed'] ?? '';
        const sorted_values = Object.keys(value_instances).sort((a, b) => value_instances[b] - value_instances[a]);

        let remaining_item_count = 0;
        let remaining_instance_count = 0;

        for(let i=0; i<sorted_values.length; i++) {
            const value = sorted_values[i];
            const count = value_instances[value] ?? 0;

            if(legend_items.length >= legend_item_limit) {
                remaining_item_count++;
                remaining_instance_count += count;
                continue;
            }

            if(count_legend_items) {
                const value_with_count = `${value} (x${count})`;
                legend_items.push({'label': value_with_count, 'color': stringToHexColor(autocolor_seed+value)});
                continue;
            }
            legend_items.push({'label': value, 'color': stringToHexColor(autocolor_seed+value)});
        }

        if(remaining_item_count > 0) {
            legend_items.push({
                'label': `+ ${remaining_item_count} more`+(count_legend_items ? ` (x${remaining_instance_count})` : ''),
                'color': null
            });
        }

        return legend_items;
    }


    const attribute_internal_name = map_settings['marker_color'];
    const attribute = staged_project.getAttribute(AssetType.Any, attribute_internal_name);
    assertNonemptyObject(attribute);
    const attribute_type = attribute.attribute_type;

    if([AttributeType.SingleOption, AttributeType.DropdownOption, AttributeType.SearchDropdown, AttributeType.ButtonSingleOption].includes(attribute_type)) {
        const value_instances = {};
        const will_overflow = attribute.options && attribute.options.length > (legend_item_limit || 0);

        if(count_legend_items || limit_legend_to_used_options || will_overflow) {
            for(const asset of staged_project.getAssets()) {
                const attrib_value = asset.getValue(attribute.internal_name);
                if(isBlankValue(attrib_value)) { continue; }
                if(!(attrib_value in value_instances)) {
                    value_instances[attrib_value] = 1;
                } else {
                    value_instances[attrib_value]++;
                }
            }
        }

        let remaining_item_count = 0;
        let remaining_instance_count = 0;
        for(const option of attribute.options) {
            if(limit_legend_to_used_options && (!(option in value_instances))) { continue; }

            const count = value_instances[option] ?? 0;

            if(legend_items.length >= legend_item_limit) {
                remaining_item_count++;
                remaining_instance_count += count;
                continue;
            }

            const color_field_key = attribute_internal_name + ':' + option;
            const color_value = staged_project.metadata.color_field_values[color_field_key];
            const symbol_value = staged_project.metadata.color_field_values['symbol:'+color_field_key];
            if(count_legend_items) {
                const option_with_count = `${option} (x${count})`;
                legend_items.push({'label': option_with_count, 'color': color_value ?? HexColor.Black, 'symbol': symbol_value});
                continue;
            }
            legend_items.push({'label': option, 'color': color_value ?? HexColor.Black, 'symbol': symbol_value});
        }

        if(remaining_item_count > 0) {
            legend_items.push({
                'label': `+ ${remaining_item_count} more`+(count_legend_items ? ` (x${remaining_instance_count})` : ''),
                'color': null
            });
        }

    } else if([AttributeType.Number, AttributeType.Integer, AttributeType.Dbh].includes(attribute_type)) {
        if(!(attribute_internal_name in window.newcollector['binned_colors_cache'])) {
            const min_color = staged_project.metadata.color_field_values[attribute_internal_name+':minimum'];
            const max_color = staged_project.metadata.color_field_values[attribute_internal_name+':maximum'];
            window.newcollector['binned_colors_cache'][attribute_internal_name] = chroma.scale([min_color, max_color]).mode('lch').colors(NUM_COLOR_BINS);
        }

        const convertLabel = ((inputNumber) => {
            if(attribute['internal_name']==='dbh') { inputNumber = convertDbhFromStoredToDisplay(inputNumber); }
            return Math.round(inputNumber);
        });

        const numeric_values = [];
        const num_bins = attribute['colorRanges'] ? attribute['colorRanges'].length : NUM_COLOR_BINS;
        const will_overflow = num_bins > (legend_item_limit || 0);

        if(count_legend_items || limit_legend_to_used_options || will_overflow) {
            for(const asset of staged_project.getAssets()) {
                const attrib_value = asset.getValue(attribute_internal_name);
                if(isBlankValue(attrib_value)) { continue; }
                numeric_values.push(Number(attrib_value));
            }
        }

        let remaining_item_count = 0;
        let remaining_instance_count = 0;

        if(map_settings['numeric_marker_color'] === 'Asset Bounds') {
            const bounds = staged_project.getBoundsOfNumericValue(attribute_internal_name);
            const color_bin_ranges = calculateBinRanges(bounds['min'], bounds['max'], NUM_COLOR_BINS);
            let remaining_numeric_values = [...numeric_values];

            for(const bin_data of color_bin_ranges) {
                let label = convertLabel(bin_data['min']) + ' - ' + convertLabel(bin_data['max']);
                const condition = v => v >= bin_data['min'] && v <= bin_data['max'];
                let count = 0;

                if(count_legend_items || limit_legend_to_used_options || will_overflow) {
                    count = remaining_numeric_values.filter(condition).length;
                    remaining_numeric_values = remaining_numeric_values.filter(v => !condition(v));
                }

                if(limit_legend_to_used_options && count === 0) { continue; }

                if(legend_items.length >= legend_item_limit) {
                    remaining_item_count++;
                    remaining_instance_count += count;
                    continue;
                }

                if(count_legend_items) { label = `${label} (x${count})`; }

                legend_items.push({
                    'label': label,
                    'color': window.newcollector['binned_colors_cache'][attribute_internal_name][bin_data['bin_index']] ?? HexColor.Black
                });
            }

        } else if('colorRanges' in attribute) {
            let remaining_numeric_values = [...numeric_values];

            for(let range_index = 0; range_index < attribute['colorRanges'].length; range_index++) {
                const range = attribute['colorRanges'][range_index];
                let label = '??';
                let condition = () => false;

                if(typeof range === 'number') {
                    if(range_index === 0) {
                        label = convertLabel(range)+'-';
                        condition = v => v <= range;
                    } else if(range_index+1 === attribute['colorRanges'].length) {
                        label = convertLabel(range)+'+';
                        condition = v => v >= range;
                    } else {
                        console.log('Unexpected non-array range '+range+' at range_index '+range_index+' (ranges length: '+attribute['colorRanges'].length+')');
                    }

                } else if(Array.isArray(range)) {
                    label = convertLabel(range[0])+' - '+convertLabel(range[1]);
                    condition = v => v >= range[0] && v <= range[1];

                } else {
                    console.error(`Unhandled range <${typeof range}> "${range}"`);
                }

                let count = 0;
                if((count_legend_items || limit_legend_to_used_options || will_overflow) && label !== '??') {
                    count = remaining_numeric_values.filter(condition).length;
                    remaining_numeric_values = remaining_numeric_values.filter(v => !condition(v));
                }

                if(limit_legend_to_used_options && label !== '??' && count === 0) { continue; }

                if(legend_items.length >= legend_item_limit) {
                    if(label !== '??') {
                        remaining_item_count++;
                        remaining_instance_count += count;
                    }
                    continue;
                }

                if(count_legend_items && label !== '??') { label = `${label} (x${count})`; }

                legend_items.push({
                    'label': label,
                    'color': window.newcollector['binned_colors_cache'][attribute_internal_name][range_index] ?? HexColor.Black
                });
            }

        } else if('colorMinVal' in attribute && 'colorMaxVal' in attribute) {
            const color_bin_ranges = calculateBinRanges(attribute['colorMinVal'], attribute['colorMaxVal'], NUM_COLOR_BINS);
            let remaining_numeric_values = [...numeric_values];

            for(const bin_data of color_bin_ranges) {
                let label = convertLabel(bin_data['min']) + ' - ' + convertLabel(bin_data['max']);
                const condition = v => v >= bin_data['min'] && v <= bin_data['max'];
                let count = 0;

                if(count_legend_items || limit_legend_to_used_options || will_overflow) {
                    count = remaining_numeric_values.filter(condition).length;
                    remaining_numeric_values = remaining_numeric_values.filter(v => !condition(v));
                }

                if(limit_legend_to_used_options && count === 0) { continue; }

                if(legend_items.length >= legend_item_limit) {
                    remaining_item_count++;
                    remaining_instance_count += count;
                    continue;
                }

                if(count_legend_items) { label = `${label} (x${count})`; }

                legend_items.push({
                    'label': label,
                    'color': window.newcollector['binned_colors_cache'][attribute_internal_name][bin_data['bin_index']] ?? HexColor.Black
                });
            }

        } else {
            console.error('No numeric legend method specified for '+attribute['internal_name']);
        }

        if(remaining_item_count > 0) {
            legend_items.push({
                'label': `+ ${remaining_item_count} more`+(count_legend_items ? ` (x${remaining_instance_count})` : ''),
                'color': null
            });
        }

    } else {
        console.log('Legend not supported for '+attribute_type);
    }
    return legend_items;
}

export const addLegendToCanvas = (opts) => {
    const ctx = opts['ctx'] ?? null;
    const map_height = opts['map_height'] ?? null;
    const map_width = opts['map_width'] ?? null;
    const legend_heading_raw = opts['legend_heading'] ?? null;
    const legend_items = opts['legend_items'] ?? null;
    const HEADING_FONT = opts['HEADING_FONT'] ?? 'bold 24px Arial';
    const LABEL_FONT = opts['LABEL_FONT'] ?? '24px Arial';
    const LEGEND_PADDING = opts['LEGEND_PADDING'] ?? 20; // Distance from edges of legend to legend content
    const CIRCLE_RADIUS = opts['CIRCLE_RADIUS'] ?? 12;
    const ROW_HEIGHT = opts['ROW_HEIGHT'] ?? 32;
    const SPACE_AFTER_CIRCLE = opts['SPACE_AFTER_CIRCLE'] ?? 10; // Horizontal pixels between color circle and label
    const LABEL_HEIGHT_ADJUST = opts['LABEL_HEIGHT_ADJUST'] ?? 1;
    const OVERLAY_BACKGROUND_COLOR = opts['OVERLAY_BACKGROUND_COLOR'] ?? 'rgba(255, 255, 255, 0.8)';
    const OVERLAY_MARGIN = opts['OVERLAY_MARGIN'] ?? 20;
    const OVERLAY_MARGIN_X = opts['OVERLAY_MARGIN_X'] ?? OVERLAY_MARGIN;
    const OVERLAY_MARGIN_Y = opts['OVERLAY_MARGIN_Y'] ?? OVERLAY_MARGIN;
    const CORNER = opts['CORNER'] ?? 'bottom-left';
    const TRUNCATE = opts['TRUNCATE'] ?? 1000;
    const TRUNCATE_HEADING = opts['TRUNCATE_HEADING'] ?? TRUNCATE;
    const TRUNCATE_LABEL = opts['TRUNCATE_LABEL'] ?? TRUNCATE;

    if(ctx === null) { console.error('Missing opt ctx'); }
    if(map_height === null) { console.error('Missing opt map_height'); }
    if(map_width === null) { console.error('Missing opt map_width'); }
    if(legend_heading_raw === null) { console.error('Missing opt legend_heading'); }
    if(legend_items === null) { console.error('Missing opt legend_items'); }

    const legend_heading = truncateString(legend_heading_raw, TRUNCATE_HEADING);

    ctx.font = LABEL_FONT;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';

    let widest_label = 0;
    for(const legend_item of legend_items) {
        const label_width = ctx.measureText(truncateString(legend_item.label, TRUNCATE_LABEL)).width;
        if(label_width > widest_label) { widest_label = label_width; }
    }

    let legend_heading_width = 0;
    /* Calculate legend heading width */ {
        ctx.font = HEADING_FONT;
        legend_heading_width = ctx.measureText(legend_heading).width + (LEGEND_PADDING * 2);
        ctx.font = LABEL_FONT;
    }

    const widest_label_width = (LEGEND_PADDING * 2) + (CIRCLE_RADIUS * 2) + SPACE_AFTER_CIRCLE + widest_label;
    const legend_width = Math.max(widest_label_width, legend_heading_width);
    const legend_height = (LEGEND_PADDING * 2) + ((legend_items.length + 1) * ROW_HEIGHT);

    const y_start = (CORNER==='bottom-left' || CORNER==='bottom-right') ?
        ((map_height - OVERLAY_MARGIN_Y - legend_height) + LEGEND_PADDING + (ROW_HEIGHT / 2)) :
        (OVERLAY_MARGIN_Y + LEGEND_PADDING + (ROW_HEIGHT / 2));

    const x_start = (CORNER==='top-left' || CORNER==='bottom-left') ?
        (OVERLAY_MARGIN_X + LEGEND_PADDING) :
        ((map_width - OVERLAY_MARGIN_X - legend_width) + LEGEND_PADDING);


    // Legend background
    ctx.fillStyle = OVERLAY_BACKGROUND_COLOR;
    ctx.fillRect(x_start - LEGEND_PADDING, y_start - (LEGEND_PADDING + (ROW_HEIGHT / 2)), legend_width, legend_height);


    // Add rows
    let legend_row = 0;

    /* Add legend heading */ {
        const y = y_start + (legend_row * ROW_HEIGHT);
        ctx.font = HEADING_FONT;
        ctx.fillStyle = HexColor.Black;
        ctx.fillText(legend_heading, x_start, y + LABEL_HEIGHT_ADJUST);
        legend_row++;
        ctx.font = LABEL_FONT;
    }

    // Add labels
    for(const legend_item of legend_items) {
        const y = y_start + (legend_row * ROW_HEIGHT);

        // Symbol
        if(legend_item.color) {
            if(legend_item.symbol && legend_item.symbol !== MapSymbol.Circle) {
                drawSymbolCanvas(
                    ctx,
                    legend_item.symbol,
                    x_start,
                    y - CIRCLE_RADIUS,
                    CIRCLE_RADIUS,
                    legend_item.color,
                    HexColor.Black,
                    1,
                    false,
                );

            } else {
                ctx.beginPath();
                ctx.arc(x_start + CIRCLE_RADIUS, y, CIRCLE_RADIUS, 0, 2 * Math.PI);
                ctx.fillStyle = legend_item.color;
                ctx.fill();
                ctx.strokeStyle = HexColor.Black;
                ctx.lineWidth = 1;
                ctx.stroke();
            }
        }

        // Label
        if(legend_item.label) {
            ctx.fillStyle = HexColor.Black;
            ctx.fillText(truncateString(legend_item.label, TRUNCATE_LABEL), x_start + (CIRCLE_RADIUS * 2) + SPACE_AFTER_CIRCLE, y + LABEL_HEIGHT_ADJUST);
            legend_row++;
        }
    }
}

export const getSourceByName = (map, name) => {
    const layers = map.getLayers().getArray();
    const layer = layers.find(l => l.get('name') === name);
    return layer ? layer.getSource() : null;
}

export const rotateCanvasAboutPoint = (ctx, radians, cx, cy) => {
    ctx.translate(cx, cy);
    ctx.rotate(radians);
    ctx.translate(-cx, -cy);
}

const SYMBOL_PATHS = {
    [MapSymbol.Circle]: {
        d: "M 38 20 A 18 18 0 1 1 2 20 A 18 18 0 1 1 38 20 Z",
    },
    [MapSymbol.Square]: {
        d: "M 2 2 L 38 2 L 38 38 L 2 38 Z",
    },
    [MapSymbol.Diamond]: {
        d: "M 20 2 L 38 20 L 20 38 L 2 20 Z",
    },
    [MapSymbol.Cross]: {
        d: "M 14 2 L 26 2 L 26 14 L 38 14 L 38 26 L 26 26 L 26 38 L 14 38 L 14 26 L 2 26 L 2 14 L 14 14 Z",
    },
    [MapSymbol.DiagonalCross]: {
        d: "M 14 2 L 26 2 L 26 14 L 38 14 L 38 26 L 26 26 L 26 38 L 14 38 L 14 26 L 2 26 L 2 14 L 14 14 Z",
        rotation: 45,
    },
    [MapSymbol.Triangle]: {
        d: "M 20 2 L 35.6 29 L 4.4 29 Z",
    },
    [MapSymbol.InvertedTriangle]: {
        d: "M 20 38 L 35.6 11 L 4.4 11 Z",
    },
    [MapSymbol.SevenPtStar]: {
        d: "M 20 2 L 23.9 11.9 L 34.1 8.8 L 28.8 18 L 37.5 24 L 27 25.6 L 27.8 36.2 L 20 29 L 12.2 36.2 L 13 25.6 L 2.5 24 L 11.2 18 L 5.9 8.8 L 16.1 11.9 Z",
    },
    [MapSymbol.SixPtStar]: {
        d: "M 20 2 L 24.5 12.2 L 35.6 11 L 29 20 L 35.6 29 L 24.5 27.8 L 20 38 L 15.5 27.8 L 4.4 29 L 11 20 L 4.4 11 L 15.5 12.2 Z",
        rotation: 90,
    },
    [MapSymbol.FivePtStar]: {
        d: "M 20 2 L 25.3 12.7 L 37.1 14.4 L 28.6 22.8 L 30.6 34.6 L 20 29 L 9.4 34.6 L 11.4 22.8 L 2.9 14.4 L 14.7 12.7 Z",
    },
    [MapSymbol.FourPtStar]: {
        d: "M 20 2 L 26.4 13.6 L 38 20 L 26.4 26.4 L 20 38 L 13.6 26.4 L 2 20 L 13.6 13.6 Z",
        rotation: 45,
    },
    [MapSymbol.ThreePtStar]: {
        d: "M 20 2 L 25.2 17 L 35.6 29 L 20 26 L 4.4 29 L 14.8 17 Z",
        rotation: 45,
    },
    [MapSymbol.SolidLine]: {
        d: "M 2 2 L 38 38",
    },
    [MapSymbol.DashedLine]: {
        d: "M 2 2 L 38 38",
        strokeDasharray: "8 8",
    },
    [MapSymbol.DottedLine]: {
        d: "M 2 2 L 38 38",
        strokeDasharray: "1 6",
        strokeLinecap: "round",
    },
};

export const getSymbolData = (symbol) => {
    if(map_symbol_cache[symbol]) { return map_symbol_cache[symbol]; }

    const def = SYMBOL_PATHS[symbol];
    if(!def) {
        console.error(`MapSymbol "${symbol}" is not implemented.`);
        return null;
    }

    const basePath = new Path2D(def.d);
    let finalPath = basePath;
    let svgTransform = '';

    if(def.rotation) {
        finalPath = new Path2D();
        const matrix = new DOMMatrix()
            .translate(20, 20)
            .rotate(def.rotation)
            .translate(-20, -20);
        finalPath.addPath(basePath, matrix);

        svgTransform = `transform="rotate(${def.rotation} 20 20)"`;
    }

    const result = {
        d: def.d,
        path2d: finalPath,
        svgTransform,
        strokeDasharray: def.strokeDasharray || '',
        strokeLinecap: def.strokeLinecap || ''
    };

    map_symbol_cache[symbol] = result;
    return result;
};

export const getSymbolSvg = (symbol, radius, marker_fill_color, marker_stroke_color, marker_stroke_width, compensate) => {
    const data = getSymbolData(symbol);
    if(!data) { return null; }

    const is_line = FEATURE_TYPE_MAP_SYMBOLS[FeatureType.Line].includes(symbol);
    const compensation = (compensate ? (MAP_SYMBOL_RADIUS_COMPENSATION[symbol] || 1.00) : 1.00);
    const size = (radius * 2) * compensation;

    const dashArrayProp = data.strokeDasharray ? `stroke-dasharray="${data.strokeDasharray}"` : '';
    const lineCapProp = data.strokeLinecap ? `stroke-linecap="${data.strokeLinecap}"` : '';

    let pathsHtml = '';

    if(is_line) {
        const inner_line_width = radius / 4;
        const outline_line_width = inner_line_width + (marker_stroke_width * 2);

        const sharedProps = `
            stroke-linejoin="round"
            vector-effect="non-scaling-stroke"
            fill="none"
            ${dashArrayProp}
            ${lineCapProp}
        `.trim();

        // Outline
        pathsHtml += `<path d="${data.d}" ${data.svgTransform} stroke="${marker_stroke_color}" stroke-width="${outline_line_width}" ${sharedProps} />`;

        // Actual line
        pathsHtml += `<path d="${data.d}" ${data.svgTransform} stroke="${marker_fill_color}" stroke-width="${inner_line_width}" ${sharedProps} />`;

    } else {
        const styleProps = `
            fill="${marker_fill_color}"
            stroke="${marker_stroke_color}"
            stroke-width="${marker_stroke_width}"
            stroke-linejoin="round"
            vector-effect="non-scaling-stroke"
            ${dashArrayProp}
            ${lineCapProp}
        `.trim();

        pathsHtml = `<path d="${data.d}" ${data.svgTransform} ${styleProps} />`;
    }

    return `
        <svg width="${size}" height="${size}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            ${pathsHtml}
        </svg>
    `.trim();
};

export const getSymbolStyle = (symbol, radius, marker_fill_color, marker_stroke_color, marker_stroke_width, compensate) => {
    if([MapSymbol.SolidLine, MapSymbol.DashedLine, MapSymbol.DottedLine].includes(symbol)) {
        const stroke_properties = {
            color: marker_fill_color,
            width: radius / 2,
        };

        if(symbol === MapSymbol.DashedLine) {
            stroke_properties['lineDash'] = [radius*3, radius*2];

        } else if(symbol === MapSymbol.DottedLine) {
            stroke_properties['lineDash'] = [1, radius*1.5];
            stroke_properties['lineCap'] = 'round';

        }

        return new Stroke(stroke_properties);
    }

    const cacheKey = `${symbol}_${radius}_${marker_fill_color}_${marker_stroke_color}_${marker_stroke_width}_${compensate}`;
    if(symbol_style_cache[cacheKey]) { return symbol_style_cache[cacheKey]; }

    const compensation = (compensate ? (MAP_SYMBOL_RADIUS_COMPENSATION[symbol] || 1.00) : 1.00);
    const size = (radius * 2) * compensation;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    drawSymbolCanvas(ctx, symbol, 0, 0, radius, marker_fill_color, marker_stroke_color, marker_stroke_width, compensate);

    const iconStyle = new Icon({
        img: canvas,
        imgSize: [size, size],
    });

    symbol_style_cache[cacheKey] = iconStyle;
    return iconStyle;
};

export const drawSymbolCanvas = (ctx, symbol, x, y, radius, marker_fill_color, marker_stroke_color, marker_stroke_width, compensate) => {
    const data = getSymbolData(symbol);
    if(!data) { return; }

    const is_line = FEATURE_TYPE_MAP_SYMBOLS[FeatureType.Line].includes(symbol);
    const compensation = (compensate ? (MAP_SYMBOL_RADIUS_COMPENSATION[symbol] || 1.00) : 1.00);
    const size = (radius * 2) * compensation;
    const scale = size / 40;

    ctx.save();

    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.lineJoin = 'round';

    if(data.strokeDasharray) {
        const scaledDashArray = data.strokeDasharray.split(' ').map(n => Number(n) / scale);
        ctx.setLineDash(scaledDashArray);
    }
    if(data.strokeLinecap) { ctx.lineCap = data.strokeLinecap; }

    if(is_line) {
        const inner_line_width = radius / 4;
        const outline_line_width = inner_line_width + (marker_stroke_width * 2);

        // Outline
        ctx.strokeStyle = marker_stroke_color;
        ctx.lineWidth = outline_line_width / scale;
        ctx.stroke(data.path2d);

        // Actual line
        ctx.strokeStyle = marker_fill_color;
        ctx.lineWidth = inner_line_width / scale;
        ctx.stroke(data.path2d);

    } else {
        ctx.fillStyle = marker_fill_color;
        ctx.strokeStyle = marker_stroke_color;
        ctx.lineWidth = marker_stroke_width / scale;

        ctx.fill(data.path2d);
        ctx.stroke(data.path2d);
    }

    ctx.restore();
};

export const getCoordsCenterpoint = (coords_to_center) => {
    // Takes nested array of WGS84 coords, ex: [[lng, lat], [lng, lat], [lng, lat]]
    // Returns array of WGS84 coords, ex: [lng, lat]

    let highest_lat = -1000;
    let lowest_lat = 1000;
    let highest_lng = -1000;
    let lowest_lng = 1000;

    for(const coord_pair of coords_to_center) {
        const lng = parseFloatStrict(coord_pair[0]);
        const lat = parseFloatStrict(coord_pair[1]);

        if(lng > highest_lng) { highest_lng = lng; }
        if(lng < lowest_lng) { lowest_lng = lng; }
        if(lat > highest_lat) { highest_lat = lat; }
        if(lat < lowest_lat) { lowest_lat = lat; }
    }

    const center_lng = (highest_lng + lowest_lng) / 2;
    const center_lat = (highest_lat + lowest_lat) / 2;
    return [center_lng, center_lat];
}

export const fromLonLatArr = (coords_arr) => {
    if(!coords_arr || coords_arr.length === 0) { return coords_arr; }
    if(typeof coords_arr[0] === 'number') { return fromLonLat(coords_arr); }
    return coords_arr.map(nested_arr => fromLonLatArr(nested_arr));
}

export const toLonLatArr = (coords_arr) => {
    if(!coords_arr || coords_arr.length === 0) { return coords_arr; }
    if(typeof coords_arr[0] === 'number') { return toLonLat(coords_arr); }
    return coords_arr.map(nested_arr => toLonLatArr(nested_arr));
}

export const formatMeasurementAmount = (num) => {
    return num.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: (num <= 100 ? 2 : (num <= 1000 ? 1 : 0)),
    });
}

export const formatInches = (total_inches, opts) => {
    const rounded_eights = Math.round(total_inches * 8);
    const whole_inches = Math.floor(rounded_eights / 8);
    const remainder = rounded_eights % 8;

    opts = opts ?? {};
    const style_start = opts['style_start'] ?? '';
    const style_end = opts['style_end'] ?? '';

    if(remainder === 0) { return style_start + whole_inches + `"` + style_end; }

    let numerator = remainder;
    let denominator = 8;
    while(numerator % 2 === 0 && denominator % 2 === 0) {
        numerator /= 2;
        denominator /= 2;
    }

    let fraction = `${numerator}/${denominator}`;
    if(fraction in FRACTION_SYMBOLS) { fraction = FRACTION_SYMBOLS[fraction]; }

    if(whole_inches === 0) { return style_start + fraction + `"` + style_end; }
    return style_start + whole_inches + ` ` + fraction + `"` + style_end;
}

export const formatFeet = (total_feet, opts) => {
    const total_eighths = Math.round(total_feet * 12 * 8);
    const feet = Math.floor(total_eighths / 96);
    const inches = (total_eighths % 96) / 8;

    opts = opts ?? {};
    const style_start = opts['style_start'] ?? '';
    const style_end = opts['style_end'] ?? '';

    if(inches === 0) { return style_start + feet + `'` + style_end; }
    if(feet === 0) { return formatInches(inches, opts); }
    return style_start + feet + `' ` + formatInches(inches, {}) + style_end;
}

export const formatMeasurements = (conversions, base_unit_amount, opts) => {
    // Levels: 1=mm, 2=in/cm, 3=ft, 4=yd/m, 5=ac/ha, 6=mi/km
    if(base_unit_amount===0) { return '0'; }
    const results = [];

    opts = opts ?? {};
    const required_levels = opts['required_levels'] ?? [conversions.find((conversion) => conversion['rate']===1)['level']];
    const optional_levels = opts['optional_levels'] ?? [];
    const style_start = opts['style_start'] ?? '';
    const style_end = opts['style_end'] ?? '';
    const max_cutoff = opts['max_cutoff'] ?? 100_000;
    const min_cutoff = opts['min_cutoff'] ?? 1;

    for(const conversion of conversions) {
        const is_required = required_levels.includes(conversion['level']);
        const is_optional = optional_levels.includes(conversion['level']);
        if(!(is_required || is_optional)) { continue; }

        const amount = base_unit_amount / conversion['rate'];
        const unit = conversion['unit'];

        if(is_optional) {
            if(unit === 'in') {
                if(Math.round(amount * 8) < min_cutoff) { continue; } // Skip if not at least 1/8th
            } else if(amount < min_cutoff) {
                continue; // Skip if less than 1
            }

            if(amount > max_cutoff) { continue; } // Skip if more than 100,000
        }

        if(unit === 'ft') {
            results.push(formatFeet(amount, opts));
        } else if(unit === 'in') {
            results.push(formatInches(amount, opts));
        } else {
            results.push(`${style_start+formatMeasurementAmount(amount)+style_end} ${unit}`);
        }
    }

    if(results.length === 0) { return ''; }
    const has_conversion_results = (results.length > 1);
    return has_conversion_results ? `${results[0]} (${results.slice(1).join(', ')})` : results[0];
}

export const displayLengthFt = (length_ft, opts) => {
    return formatMeasurements(
        [
            { 'rate': 5280, 'unit': 'mi', 'level': 6 },
            { 'rate': 3, 'unit': 'yd', 'level': 4 },
            { 'rate': 1, 'unit': 'ft', 'level': 3 },
            { 'rate': 1/12, 'unit': 'in', 'level': 2 },
        ],
        length_ft,
        opts,
    );
}

export const displayLengthM = (length_m, opts) => {
    return formatMeasurements(
        [
            { 'rate': 1000, 'unit': 'km', 'level': 6 },
            { 'rate': 1, 'unit': 'm', 'level': 4 },
            { 'rate': 0.01, 'unit': 'cm', 'level': 2 },
            { 'rate': 0.001, 'unit': 'mm', 'level': 1 },
        ],
        length_m,
        opts,
    );
}

export const displayLength = (length_m, opts) => {
    const units = window?.newcollector?.settings?.units || 'Metric';
    if(units === 'Metric') {
        return displayLengthM(length_m, opts);
    } else {
        return displayLengthFt(length_m * 3.28084, opts);
    }
}

export const displayAreaSqFt = (area_sq_ft, opts) => {
    return formatMeasurements(
        [
            { 'rate': 27878400, 'unit': 'sq mi', 'level': 6 },
            { 'rate': 43560, 'unit': 'ac', 'level': 5 },
            { 'rate': 9, 'unit': 'sq yd', 'level': 4 },
            { 'rate': 1, 'unit': 'sq ft', 'level': 3 },
            { 'rate': 1/144, 'unit': 'sq in', 'level': 2 },
        ],
        area_sq_ft,
        opts,
    );
}

export const displayAreaSqM = (area_sq_m, opts) => {
    return formatMeasurements(
        [
            { 'rate': 1000000, 'unit': 'sq km', 'level': 6 },
            { 'rate': 10000, 'unit': 'ha', 'level': 5 },
            { 'rate': 1, 'unit': 'sq m', 'level': 4 },
            { 'rate': 0.0001, 'unit': 'sq cm', 'level': 2 },
            { 'rate': 0.000001, 'unit': 'sq mm', 'level': 1 },
        ],
        area_sq_m,
        opts,
    );
}

export const displayArea = (area_sq_m, opts) => {
    const units = window?.newcollector?.settings?.units || 'Metric';
    if(units === 'Metric') {
        return displayAreaSqM(area_sq_m, opts);
    } else {
        return displayAreaSqFt(area_sq_m * 10.76391, opts);
    }
}

export const displayVolumeCuFt = (volume_cu_ft, opts) => {
    return formatMeasurements(
        [
            { 'rate': 147197952000, 'unit': 'cu mi', 'level': 6 },
            { 'rate': 43560, 'unit': 'ac-ft', 'level': 5 },
            { 'rate': 27, 'unit': 'cu yd', 'level': 4 },
            { 'rate': 1, 'unit': 'cu ft', 'level': 3 },
            { 'rate': 1/1728, 'unit': 'cu in', 'level': 2 },
        ],
        volume_cu_ft,
        opts,
    );
}

export const displayVolumeCuM = (volume_cu_m, opts) => {
    return formatMeasurements(
        [
            { 'rate': 1000000000, 'unit': 'cu km', 'level': 6 },
            { 'rate': 1, 'unit': 'cu m', 'level': 4 },
            { 'rate': 0.000001, 'unit': 'cu cm', 'level': 2 },
            { 'rate': 0.000000001, 'unit': 'cu mm', 'level': 1 },
        ],
        volume_cu_m,
        opts,
    );
}

export const displayVolume = (volume_cu_m, opts) => {
    const units = window?.newcollector?.settings?.units || 'Metric';
    if(units === 'Metric') {
        return displayVolumeCuM(volume_cu_m, opts);
    } else {
        return displayVolumeCuFt(volume_cu_m * 35.3147, opts);
    }
}

export const contextMenuCallback_getCoordinates = (async (lng, lat) => {
    const popup = new InfoModal();
    popup.appendHtml(`
        <div class="d-flex justify-content-center gap-2">
            <strong>Lng, Lat:</strong>
            <span>${lng.toFixed(5)}, ${lat.toFixed(5)}</span>
        </div>
    `);

    const button_container = document.createElement('div');
    button_container.classList.add('d-flex', 'justify-content-center', 'gap-2');
    popup.appendNode(button_container);

    const copy_button_lnglat = new Button('Copy lng,lat', ['btn-sm', 'btn-info', 'my-2']);
    copy_button_lnglat.elem.style.minWidth = '120px';
    copy_button_lnglat.onClick(async () => {
        copy_button_lnglat.disable();
        await navigator.clipboard.writeText(`${lng}, ${lat}`);
        copy_button_lnglat.flashSuccess();
        copy_button_lnglat.enable();
    });
    button_container.appendChild(copy_button_lnglat.elem);

    const copy_button_latlng = new Button('Copy lat,lng', ['btn-sm', 'btn-info', 'my-2']);
    copy_button_latlng.elem.style.minWidth = '120px';
    copy_button_latlng.onClick(async () => {
        copy_button_latlng.disable();
        await navigator.clipboard.writeText(`${lat}, ${lng}`);
        copy_button_latlng.flashSuccess();
        copy_button_latlng.enable();
    });
    button_container.appendChild(copy_button_latlng.elem);

    popup.show();
});

export const contextMenuCallback_getNearestAddress = (async (lng, lat) => {
    const popup = new InfoModal(`<h5>Loading...</h5>`);
    popup.show();

    const address = await getAddressFromCoords({ 'lng': lng, 'lat': lat });
    popup.clearBody();
    popup.appendHtml(`
        <div class="d-flex justify-content-center gap-2">
            <strong>Address:</strong>
            <span>${address}</span>
        </div>
    `);

    const copy_button = new Button('Copy address', ['btn-sm', 'btn-info', 'my-2', 'centered-reasonable-width']);
    copy_button.onClick(async () => {
        copy_button.disable();
        await navigator.clipboard.writeText(address);
        copy_button.flashSuccess();
        copy_button.enable();
    });
    popup.appendNode(copy_button.elem);
});

const handleMultiPolygonSplit = async (map_page, feature, asset, multiPolygonCoords) => {
    const poly_parts = multiPolygonCoords.map(coords => ({
        coords: coords,
        area: turfArea(turfPolygon(coords))
    }));

    poly_parts.sort((a, b) => b.area - a.area);

    const largest_coords = poly_parts[0].coords;
    feature.setGeometry(new Polygon(fromLonLatArr(largest_coords)));
    await map_page.moveAsset(feature, asset, largest_coords);

    for(let i = 1; i < poly_parts.length; i++) {
        const cloned_asset = new Asset(genId());
        cloned_asset.setProject(map_page.staged_project);
        cloned_asset.setData(cloneDeep(asset.getData()));
        cloned_asset.setValue('coordinates', poly_parts[i].coords);
        cloned_asset.setValue('time_modified', currentUnixTimestamp());

        if(asset.getType() === AssetType.Tree) {
            const tree_id = map_page.staged_project.getNextAssetAutoIncrement();
            cloned_asset.setValue('tree_id', tree_id);
            map_page.staged_project.markAutoIncrementUsed(tree_id);
        }

        await map_page.staged_project.newAsset(cloned_asset);
        map_page.addAssetToMap(cloned_asset.asset_id, cloned_asset);
    }

    await map_page.staged_project.saveOnClient();
    map_page.renderOverlayLegend();

    if(map_page.map_save_button) { map_page.map_save_button.classList.add('unsaved-changes'); }
};

export const contextMenuCallback_move = ((map_page, feature, asset, lng, lat, clone) => {
    map_page._is_moving_feature = true;

    const original_geometry = feature.getGeometry();
    const temp_hover_feature = feature.clone();

    temp_hover_feature.set('is_temp_draw:feature', true);
    map_page.marker_source.addFeature(temp_hover_feature);

    const start_coord_proj = fromLonLat([lng, lat]);
    let current_hover_geometry = original_geometry.clone();

    const pointerMoveHandler = ((event) => {
        const current_coord_proj = event.coordinate;
        const delta_x = current_coord_proj[0] - start_coord_proj[0];
        const delta_y = current_coord_proj[1] - start_coord_proj[1];
        const new_geom = original_geometry.clone();
        new_geom.translate(delta_x, delta_y);
        temp_hover_feature.setGeometry(new_geom);
        current_hover_geometry = new_geom;
    });

    const clickHandler = (async (event) => {
        map_page.map.un('pointermove', pointerMoveHandler);
        map_page.map.un('click', clickHandler);
        document.removeEventListener('keydown', escHandler);

        map_page.marker_source.removeFeature(temp_hover_feature);

        const hover_coordinates = current_hover_geometry.getCoordinates();
        const asset_feature_type = asset.getFeatureType();
        const new_asset_coords = (asset_feature_type === FeatureType.Point ? toLonLat : toLonLatArr)(hover_coordinates);

        if(clone) {
            const cloned_asset = new Asset(genId());
            cloned_asset.setProject(map_page.staged_project);
            cloned_asset.setData(cloneDeep(asset.getData()));
            cloned_asset.setValue('coordinates', new_asset_coords);
            cloned_asset.setValue('time_modified', currentUnixTimestamp());

            if(asset.getType()===AssetType.Tree) {
                // This auto-increment ID logic is also done in editAssetModal
                const tree_id = map_page.staged_project.getNextAssetAutoIncrement();
                cloned_asset.setValue('tree_id', tree_id);
                map_page.staged_project.markAutoIncrementUsed(tree_id);
            }

            await map_page.staged_project.newAsset(cloned_asset);
            await map_page.staged_project.saveOnClient();

            map_page.addAssetToMap(cloned_asset.asset_id, cloned_asset);
            map_page.renderOverlayLegend();
            if(map_page.map_save_button) { map_page.map_save_button.classList.add('unsaved-changes'); }

        } else {
            feature.setGeometry(current_hover_geometry);
            await map_page.moveAsset(feature, asset, new_asset_coords);

        }

        setTimeout(() => { map_page._is_moving_feature = false; }, 50);
    });

    const escHandler = ((event) => {
        if(event.key !== 'Escape') { return; }
        map_page.map.un('pointermove', pointerMoveHandler);
        map_page.map.un('click', clickHandler);
        document.removeEventListener('keydown', escHandler);
        map_page.marker_source.removeFeature(temp_hover_feature);
        setTimeout(() => { map_page._is_moving_feature = false; }, 50);
    });

    map_page.map.on('pointermove', pointerMoveHandler);
    document.addEventListener('keydown', escHandler);

    setTimeout(() => { map_page.map.on('click', clickHandler); }, 50);
});

export const contextMenuCallback_delete = ((map_page, feature, asset) => {
    const asset_name = asset.getName();
    const confirmation_popup = new ConfirmationModal(`Delete ${asset_name}`);
    confirmation_popup.appendHtml(`Are you sure you want to permanently delete this ${asset_name}?`);
    confirmation_popup.onConfirm(async () => {
        confirmation_popup.hideCloseButton();
        confirmation_popup.disableConfirmationButton();
        confirmation_popup.startLoading();
        await map_page.staged_project.deleteAsset(asset.asset_id);
        await map_page.staged_project.saveOnClient();
        map_page.assetChanged(feature, asset.asset_id, true);
        confirmation_popup.remove();
    });
    confirmation_popup.show();
});

export const contextMenuCallback_cutHole = ((map_page, feature, asset) => {
    map_page._previous_asset_type = map_page.current_asset_type;
    map_page.current_asset_type = '';
    map_page.current_feature_type = FeatureType.Polygon;


    map_page.drawing.startDrawing({
        feature_type: FeatureType.Polygon,
        purpose: 'hole',
        initial_coordinates: null,
        save_callback: async (event, new_feature, normalized_coords) => {
            const did_save = await awaitConfirmation(null, 'Are you finished drawing cutout hole?');
            if(!did_save) { return false; }

            try {
                const asset_coords = asset.getValue('coordinates');
                const turf_base_polygon = turfPolygon(asset_coords);
                const is_multipolygon = Array.isArray(normalized_coords[0][0][0]);
                const turf_cutout_polygon = (is_multipolygon ? turfMultiPolygon : turfPolygon)(normalized_coords);
                const turf_diff_feature = turfDifference(turfFeatureCollection([turf_base_polygon, turf_cutout_polygon]));

                if(!turf_diff_feature) {
                    new ErrorModal('That cutout would completely remove the polygon. Delete the feature instead.').show();
                    return false;
                }

                if(turf_diff_feature.geometry.type === 'MultiPolygon') {
                    const confirm_split = await awaitConfirmation(null, 'That cutout will split the polygon into multiple parts. Do you want to continue and separate them into new assets?');
                    if(!confirm_split) { return false; }
                    await handleMultiPolygonSplit(map_page, feature, asset, turf_diff_feature.geometry.coordinates);
                } else {
                    const new_asset_coords = turf_diff_feature.geometry.coordinates;
                    feature.setGeometry(new Polygon(fromLonLatArr(new_asset_coords)));
                    await map_page.moveAsset(feature, asset, new_asset_coords);
                }

            } catch (err) {
                console.error('Error calculating polygon difference:', err);
                sentryCaptureException(err);
                return false;
            }

            map_page.drawing.stopDrawing();
            map_page.setCurrentAssetType(map_page._previous_asset_type);
            map_page._previous_asset_type = undefined;
            return true;
        }
    });
});

export const contextMenuCallback_addPatch = ((map_page, feature, asset) => {
    map_page._previous_asset_type = map_page.current_asset_type;
    map_page.current_asset_type = '';
    map_page.current_feature_type = FeatureType.Polygon;

    map_page.drawing.startDrawing({
        feature_type: FeatureType.Polygon,
        purpose: 'add_polygon',
        initial_coordinates: null,
        save_callback: async (event, new_feature, normalized_coords) => {
            const did_save = await awaitConfirmation(null, 'Are you finished drawing the polygon addition?');
            if(!did_save) { return false; }

            try {
                const asset_coords = asset.getValue('coordinates');
                const turf_base_polygon = turfPolygon(asset_coords);
                const is_multipolygon = Array.isArray(normalized_coords[0][0][0]);
                const turf_added_polygon = (is_multipolygon ? turfMultiPolygon : turfPolygon)(normalized_coords);

                const turf_union_feature = turfUnion(turfFeatureCollection([turf_base_polygon, turf_added_polygon]));

                if(!turf_union_feature) {
                    new ErrorModal('Could not merge the polygons. Try again.').show();
                    return false;
                }

                if(turf_union_feature.geometry.type === 'MultiPolygon') {
                    const confirm_split = await awaitConfirmation(null, 'The added patch is separate from the main polygon. Do you want to continue and create a new asset for it?');
                    if(!confirm_split) { return false; }
                    await handleMultiPolygonSplit(map_page, feature, asset, turf_union_feature.geometry.coordinates);
                } else {
                    const new_asset_coords = turf_union_feature.geometry.coordinates;
                    feature.setGeometry(new Polygon(fromLonLatArr(new_asset_coords)));
                    await map_page.moveAsset(feature, asset, new_asset_coords);
                }

            } catch (err) {
                console.error('Error calculating polygon union:', err);
                sentryCaptureException(err);
                return false;
            }

            map_page.drawing.stopDrawing();
            map_page.setCurrentAssetType(map_page._previous_asset_type);
            map_page._previous_asset_type = undefined;
            return true;
        }
    });
});

export const contextMenuCallback_extendBeginning = ((map_page, feature, asset) => {
    map_page._previous_asset_type = map_page.current_asset_type;
    map_page.current_asset_type = '';
    map_page.current_feature_type = FeatureType.Line;

    const existing_coords = asset.getValue('coordinates');
    const start_point = existing_coords[0];

    map_page.drawing.startDrawing({
        feature_type: FeatureType.Line,
        purpose: 'extend_line',
        initial_coordinates: start_point,
        save_callback: async (event, new_feature, normalized_coords) => {
            const did_save = await awaitConfirmation(null, 'Are you finished extending the line?');
            if(!did_save) { return false; }

            const reversed_drawn = [...normalized_coords].reverse();
            const new_asset_coords = [...reversed_drawn, ...existing_coords.slice(1)];

            feature.setGeometry(new LineString(fromLonLatArr(new_asset_coords)));
            await map_page.moveAsset(feature, asset, new_asset_coords);

            map_page.drawing.stopDrawing();
            map_page.setCurrentAssetType(map_page._previous_asset_type);
            map_page._previous_asset_type = undefined;
            return true;
        }
    });
});

export const contextMenuCallback_extendEnd = ((map_page, feature, asset) => {
    map_page._previous_asset_type = map_page.current_asset_type;
    map_page.current_asset_type = '';
    map_page.current_feature_type = FeatureType.Line;

    const existing_coords = asset.getValue('coordinates');
    const end_point = existing_coords[existing_coords.length - 1];

    map_page.drawing.startDrawing({
        feature_type: FeatureType.Line,
        purpose: 'extend_line',
        initial_coordinates: end_point,
        save_callback: async (event, new_feature, normalized_coords) => {
            const did_save = await awaitConfirmation(null, 'Are you finished extending the line?');
            if(!did_save) { return false; }

            const new_asset_coords = [...existing_coords.slice(0, -1), ...normalized_coords];

            feature.setGeometry(new LineString(fromLonLatArr(new_asset_coords)));
            await map_page.moveAsset(feature, asset, new_asset_coords);

            map_page.drawing.stopDrawing();
            map_page.setCurrentAssetType(map_page._previous_asset_type);
            map_page._previous_asset_type = undefined;
            return true;
        }
    });
});

export const contextMenuCallback_editVertices = ((map_page, feature, asset) => {
    if(map_page._is_editing_vertices) { return; }
    map_page._is_editing_vertices = true;

    const original_geometry = feature.getGeometry().clone();
    let finish_editing_control;

    const modify_collection = new Collection([feature]);
    const modify_interaction = new Modify({
        features: modify_collection,
    });

    map_page.map.addInteraction(modify_interaction);

    const saveVertices = (async () => {
        const did_save = await awaitConfirmation(null, 'Are you finished editing the vertices?');
        if(!did_save) { return; }

        document.removeEventListener('keydown', keyDownHandler);
        map_page.map.removeInteraction(modify_interaction);
        map_page._is_editing_vertices = false;

        if(finish_editing_control) {
            finish_editing_control.hide();
            map_page.map.removeControl(finish_editing_control);
        }

        const geometry = feature.getGeometry();
        const new_asset_coords = toLonLatArr(geometry.getCoordinates());
        await map_page.moveAsset(feature, asset, new_asset_coords);
    });

    const keyDownHandler = ((event) => {
        if(modalIsOpen()) { return; }
        if(event.key === 'Escape') {
            document.removeEventListener('keydown', keyDownHandler);

            map_page.map.removeInteraction(modify_interaction);

            if(finish_editing_control) {
                finish_editing_control.hide();
                map_page.map.removeControl(finish_editing_control);
            }

            feature.setGeometry(original_geometry);
            map_page._is_editing_vertices = false;

        } else if(event.key === 'Enter') {
            saveVertices();

        }
    });
    document.addEventListener('keydown', keyDownHandler);

    finish_editing_control = new CallbackControl({
        map_page: map_page,
        side: 'center',
        row: 3,
        column: 2,
        width: 2.5,
        name: 'Save Vertices',
        text: 'Save Vertices',
        icon: 'check-square',
        callback: saveVertices,
    });
    finish_editing_control.button.classList.add('asset-type-toggle', 'unsaved-changes');
    map_page.map.addControl(finish_editing_control);
    finish_editing_control.show();
});

export const leftOrMiddleClickPan = (map_browser_event) => {
    const event = map_browser_event['originalEvent'];
    if(!event || !noModifierKeys(map_browser_event)) { return false; }
    return (event.button===0 || event.button===1);
}


export const flattenPolygonCoords = (coords) => {
    if(!coords || coords.length === 0) {
        console.log('[flattenPolygonCoords] Was passed bad coords:', coords);
        return coords;
    }

    const reader = new jstsGeojsonReader();
    const writer = new jstsGeojsonWriter();

    try {
        const jstsGeom = reader.read({ type: 'Polygon', coordinates: coords });
        const flattenedGeom = jstsBufferOp.bufferOp(jstsGeom, 0);
        const resultGeoJSON = writer.write(flattenedGeom);

        if(!resultGeoJSON.coordinates || resultGeoJSON.coordinates.length === 0) {
            console.log('[flattenPolygonCoords] Got bad geometry:', resultGeoJSON.coordinates);
            return coords;
        }

        if(resultGeoJSON.type === 'MultiPolygon') {
            console.log('[flattenPolygonCoords] MultiPolygon, creating bridge');
            let combinedOuterRing = [];
            const combinedHoles = [];

            resultGeoJSON.coordinates.forEach((polyCoords, index) => {
                const outerRing = polyCoords[0];
                const holes = polyCoords.slice(1);

                if(index === 0) {
                    combinedOuterRing = [...outerRing];

                } else {
                    const bridgeStartPoint = combinedOuterRing[combinedOuterRing.length - 1];
                    combinedOuterRing.push(...outerRing);
                    combinedOuterRing.push([...bridgeStartPoint]);
                }

                combinedHoles.push(...holes);
            });

            return [combinedOuterRing, ...combinedHoles];
        }

        return resultGeoJSON.coordinates;

    } catch (error) {
        console.error('Failed to flatten polygon coordinates, falling back to original:', error);
        return coords;
    }
};

export const contextMenuCallback_flattenPolygon = (async (map_page, feature, asset) => {
    const geometry = feature.getGeometry();
    const new_asset_coords = flattenPolygonCoords(toLonLatArr(geometry.getCoordinates()));
    geometry.setCoordinates(fromLonLatArr(new_asset_coords));
    await map_page.moveAsset(feature, asset, new_asset_coords);
});